import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import "./Styles/FormList.css";
import {
    faEdit, faTrash, faSave, faSearch, faRefresh, faSpinner, faCalculator, faFileInvoice, faMoneyBill, faBalanceScale, faBoxes,
    faExclamationTriangle, faCheckCircle, faTimes, faChevronLeft,
    faChevronRight, faFilePdf, faCalendar, faUser, faStore, faPhone,
    faEnvelope, faMapMarkerAlt, faTag, faExchangeAlt, faChevronDown,
    faExpand, faCompress, faArrowsAlt, faMinus, faPlus, faFilter, faEyeSlash,
    faShoppingCart, faEye, faPrint, faDollarSign, faCreditCard, faWallet,
    faReceipt, faHistory, faInfoCircle, faBarcode
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode';
import { SaleItemsTable, useBarcodeScanner, buildSaleItem } from './solar_Sell';
import '../styles/LanguageTypography.css';
import { translateTextBatch } from '../hooks/useUniversalTranslation';
import { translateSavedRows } from '../utils/translateRecords';
// import './SellList-edit.css';

const SELL_API = 'https://ourzestapp.online/oz_backend/solar_module/solar_sell.php';
const RTL_LANGUAGE_CODES = new Set(['ur', 'ps', 'ar', 'fa', 'prs']);
const sourceValue = (record, field, fallback = '') =>
    record?._original?.[field] ?? record?.[field] ?? fallback;
const WARRANTY_VERIFY_URL = 'https://ourzestapp.online/oz_backend/solar_module/sales_warranty_verification.php';

const BUSINESS_INFO_API = 'https://ourzestapp.online/oz_backend/get_business_info.php';
const businessInfoRequestCache = new Map();

const getBusinessInfoOnce = (username) => {
    const cacheKey = String(username || '').trim();
    const cached = businessInfoRequestCache.get(cacheKey);
    const now = Date.now();

    if (cached && now - cached.createdAt < 30000) {
        return cached.promise;
    }

    // Use GET request with action parameter
    const url = `${BUSINESS_INFO_API}?action=get_business_info&username=${encodeURIComponent(cacheKey)}`;
    const promise = fetch(url, {
        method: 'GET',
        headers: {
            'Accept': 'application/json'
        },
        credentials: 'include'
    }).then(async (response) => {
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const text = await response.text();
        if (!text || text.trim() === '') {
            throw new Error('No data received');
        }

        let data;
        try {
            data = JSON.parse(text);
        } catch (e) {
            console.error('Failed to parse JSON:', text.substring(0, 200));
            throw new Error('Invalid JSON response from server');
        }

        // Check if the response has the expected data
        // The API returns status: "active" for success
        if (data.id && data.username) {
            // Transform the response to match expected format
            return {
                status: 'success',
                ...data
            };
        }

        // If there's an error field
        if (data.status === 'error' || data.message) {
            throw new Error(data.message || 'Business information request failed');
        }

        // If we got data but no id, it might be an error
        if (!data.id) {
            throw new Error('No user data found');
        }

        return data;
    });

    businessInfoRequestCache.set(cacheKey, { createdAt: now, promise });
    promise.catch(() => businessInfoRequestCache.delete(cacheKey));
    return promise;
};

const hasIssuedWarranty = (item) => (
    Number(item?.WarrantyStatus ?? item?.warranty_status ?? 0) === 1 && Boolean(item?.warranty)
);

// Helper function to adjust color brightness
const adjustColor = (color, amount) => {
    if (!color || !/^#[0-9A-F]{6}$/i.test(color)) return '#6366f1';
    let r = parseInt(color.slice(1, 3), 16);
    let g = parseInt(color.slice(3, 5), 16);
    let b = parseInt(color.slice(5, 7), 16);
    r = Math.max(0, Math.min(255, r + amount));
    g = Math.max(0, Math.min(255, g + amount));
    b = Math.max(0, Math.min(255, b + amount));
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// Split a business name into a bold first line (first two words) and a
// quieter second line while keeping single/short names graceful.
const splitBusinessName = (name = '') => {
    const words = String(name).trim().split(/\s+/).filter(Boolean);
    const secondaryWords = words.slice(2);
    return {
        primary: words.slice(0, 2).join(' '),
        secondary: secondaryWords.join(' '),
        // Two words are spread word-to-word; one word is spread letter-to-letter.
        secondaryUnits: secondaryWords.length === 1
            ? Array.from(secondaryWords[0])
            : secondaryWords
    };
};

// ============================================
// SHARED TRANSLATOR
// ============================================
const createSalesListTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) { }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
        } catch (e) { }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        const dict = {
            'ur': {
                'Sales List': 'سیلز کی فہرست',
                'All sales details are below': 'تمام سیلز کی تفصیلات نیچے ہیں',
                'Bill Number': 'بل نمبر',
                'BN': 'BN',
                'Customer Name': 'کسٹمر کا نام',
                'Phone': 'فون نمبر',
                'Date': 'تاریخ',
                'Grand Total': 'کل رقم',
                'Paid Amount': 'ادا کردہ رقم',
                'Balance': 'باقی رقم',
                'Payment Method': 'ادائیگی کا طریقہ',
                'Payment Status': 'ادائیگی کی حیثیت',
                'Actions': 'اعمال',
                'Added By': 'شامل کرنے والا',
                'Search sales...': 'سیلز تلاش کریں...',
                'Refresh': 'ریفریش کریں',
                'Export PDF': 'پی ڈی ایف ایکسپورٹ کریں',
                'Edit': 'ترمیم کریں',
                'Delete': 'حذف کریں',
                'View': 'دیکھیں',
                'Print': 'پرنٹ کریں',
                'Close': 'بند کریں',
                'Save Changes': 'تبدیلیاں محفوظ کریں',
                'Cancel': 'منسوخ کریں',
                'Success': 'کامیابی',
                'Error': 'خرابی',
                'Warning': 'انتباہ',
                'Showing': 'دکھا رہا ہے',
                'of': 'میں سے',
                'Previous': 'پچھلا',
                'Next': 'اگلا',
                'Switch Shop': 'شاپ تبدیل کریں',
                'No shop selected': 'کوئی شاپ منتخب نہیں',
                'Please select a shop to view sales': 'براہ کرم سیلز دیکھنے کے لیے شاپ منتخب کریں',
                'S.No': 'نمبر',
                'Sales': 'سیلز',
                'Shop': 'شاپ',
                'Sale Details': 'سیل کی تفصیلات',
                'Items': 'آئٹمز',
                'Product': 'پروڈکٹ',
                'Quantity': 'مقدار',
                'Price': 'قیمت',
                'Total': 'کل',
                'Subtotal': 'ذیلی کل',
                'Tax': 'ٹیکس',
                'Discount': 'ڈسکاؤنٹ',
                'Update Sale': 'سیل اپ ڈیٹ کریں',
                'Updating...': 'اپ ڈیٹ ہو رہا ہے...',
                'Sale updated successfully': 'سیل کامیابی سے اپ ڈیٹ ہو گئی',
                'Error updating sale': 'سیل اپ ڈیٹ کرنے میں خرابی',
                'Confirm Delete': 'حذف کرنے کی تصدیق',
                'Yes, Delete': 'ہاں، حذف کریں',
                'No, Cancel': 'نہیں، منسوخ کریں',
                'Portrait': 'عمودی',
                'Landscape': 'افقی',
                'A4': 'A4',
                'Letter': 'لیٹر',
                'Legal': 'لیگل',
                'A5': 'A5',
                'Sales Report': 'سیلز رپورٹ',
                'Sales Directory': 'سیلز ڈائریکٹری',
                'Total Sales': 'کل سیلز',
                'Generated': 'تیار کردہ',
                'Report ID': 'رپورٹ آئی ڈی',
                'Generated by': 'تیار کردہ بذریعہ',
                'Shop Code': 'شاپ کوڈ',
                'FILTERS APPLIED': 'فلٹرز لاگو ہیں',
                'Search': 'تلاش کریں',
                'From': 'سے',
                'To': 'تک',
                'Page': 'صفحہ',
                'Paid': 'ادا شدہ',
                'Unpaid': 'غیر ادا شدہ',
                'Partially Paid': 'جزوی ادا شدہ',
                'All': 'سب',
                'Bill Number Filter': 'بل نمبر',
                'Customer Name Filter': 'کسٹمر کا نام',
                'Phone Filter': 'فون نمبر',
                'Added By Filter': 'شامل کرنے والا',
                'Date Range': 'تاریخ کی حد',
                'Start Date': 'شروع تاریخ',
                'End Date': 'اختتام تاریخ',
                'Payment Status Filter': 'ادائیگی کی حیثیت',
                'Filter By': 'فلٹر کریں بذریعہ',
                'Orientation': 'اورینٹیشن',
                'Page Size': 'صفحہ کا سائز',
                'Cash': 'نقد',
                'Card': 'کارڈ',
                'UPI': 'یوپی آئی',
                'Bank Transfer': 'بینک ٹرانسفر'
            },
            'ar': {
                'Sales List': 'قائمة المبيعات',
                'All sales details are below': 'جميع تفاصيل المبيعات أدناه',
                'Bill Number': 'رقم الفاتورة',
                'BN': 'BN',
                'Customer Name': 'اسم العميل',
                'Phone': 'رقم الهاتف',
                'Date': 'التاريخ',
                'Grand Total': 'المجموع الكلي',
                'Paid Amount': 'المبلغ المدفوع',
                'Balance': 'الرصيد',
                'Payment Method': 'طريقة الدفع',
                'Payment Status': 'حالة الدفع',
                'Actions': 'الإجراءات',
                'Added By': 'أضيف بواسطة',
                'Search sales...': 'بحث عن مبيعات...',
                'Refresh': 'تحديث',
                'Export PDF': 'تصدير PDF',
                'Edit': 'تعديل',
                'Delete': 'حذف',
                'View': 'عرض',
                'Print': 'طباعة',
                'Close': 'إغلاق',
                'Save Changes': 'حفظ التغييرات',
                'Cancel': 'إلغاء',
                'Success': 'نجاح',
                'Error': 'خطأ',
                'Warning': 'تحذير',
                'Showing': 'عرض',
                'of': 'من',
                'Previous': 'السابق',
                'Next': 'التالي',
                'Switch Shop': 'تبديل المتجر',
                'No shop selected': 'لم يتم اختيار متجر',
                'Please select a shop to view sales': 'الرجاء اختيار متجر لعرض المبيعات',
                'S.No': 'رقم',
                'Sales': 'مبيعات',
                'Shop': 'متجر',
                'Sale Details': 'تفاصيل البيع',
                'Items': 'العناصر',
                'Product': 'المنتج',
                'Quantity': 'الكمية',
                'Price': 'السعر',
                'Total': 'المجموع',
                'Subtotal': 'المجموع الفرعي',
                'Tax': 'الضريبة',
                'Discount': 'الخصم',
                'Update Sale': 'تحديث البيع',
                'Updating...': 'جاري التحديث...',
                'Sale updated successfully': 'تم تحديث البيع بنجاح',
                'Error updating sale': 'خطأ في تحديث البيع',
                'Confirm Delete': 'تأكيد الحذف',
                'Yes, Delete': 'نعم، حذف',
                'No, Cancel': 'لا، إلغاء',
                'Portrait': 'عمودي',
                'Landscape': 'أفقي',
                'A4': 'A4',
                'Letter': 'رسالة',
                'Legal': 'قانوني',
                'A5': 'A5',
                'Sales Report': 'تقرير المبيعات',
                'Sales Directory': 'دليل المبيعات',
                'Total Sales': 'إجمالي المبيعات',
                'Generated': 'تم الإنشاء',
                'Report ID': 'معرف التقرير',
                'Generated by': 'تم الإنشاء بواسطة',
                'Shop Code': 'رمز المتجر',
                'FILTERS APPLIED': 'المرشحات المطبقة',
                'Search': 'بحث',
                'From': 'من',
                'To': 'إلى',
                'Page': 'صفحة',
                'Paid': 'مدفوع',
                'Unpaid': 'غير مدفوع',
                'Partially Paid': 'مدفوع جزئياً',
                'All': 'الكل',
                'Bill Number Filter': 'رقم الفاتورة',
                'Customer Name Filter': 'اسم العميل',
                'Phone Filter': 'رقم الهاتف',
                'Added By Filter': 'أضيف بواسطة',
                'Date Range': 'نطاق التاريخ',
                'Start Date': 'تاريخ البدء',
                'End Date': 'تاريخ الانتهاء',
                'Payment Status Filter': 'حالة الدفع',
                'Filter By': 'تصفية حسب',
                'Orientation': 'الاتجاه',
                'Page Size': 'حجم الصفحة',
                'Cash': 'نقدي',
                'Card': 'بطاقة',
                'UPI': 'UPI',
                'Bank Transfer': 'تحويل بنكي'
            }
        };

        if (dict[targetLang] && dict[targetLang][text]) {
            return dict[targetLang][text];
        }

        const [translated] = await translateTextBatch([text], targetLang, 'en');
        return translated || text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;
        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }
        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();
        return translated;
    };

    const defaultTexts = {
        salesList: 'Sales List',
        formDetails: 'All sales details are below',
        sales: 'Sales',
        shopName: 'Shop',
        noShopSelected: 'No shop selected. Please select a shop first.',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        searchPlaceholder: 'Search sales...',
        refresh: 'Refresh',
        loadingUserData: 'Loading your sales data...',
        selectShop: 'Please select a shop to view sales',
        billNumber: 'Bill Number',
        bn: 'BN',
        customerName: 'Customer Name',
        phone: 'Phone',
        date: 'Date',
        grandTotal: 'Grand Total',
        paidAmount: 'Paid Amount',
        balance: 'Balance',
        paymentMethod: 'Payment Method',
        paymentStatus: 'Status',
        actions: 'Actions',
        addedBy: 'Added By',
        edit: 'Edit',
        delete: 'Delete',
        view: 'View',
        print: 'Print',
        serialNo: 'S.No',
        showing: 'Showing',
        of: 'of',
        previous: 'Previous',
        next: 'Next',
        all: 'All',
        billNumberFilter: 'Bill Number',
        customerNameFilter: 'Customer Name',
        phoneFilter: 'Phone Number',
        addedByFilter: 'Added By',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        paymentStatusFilter: 'Payment Status',
        filterBy: 'Filter By',
        exportToPDF: 'Export PDF',
        orientation: 'Orientation',
        portrait: 'Portrait',
        landscape: 'Landscape',
        pageSize: 'Page Size',
        a4: 'A4',
        letter: 'Letter',
        legal: 'Legal',
        a5: 'A5',
        saleDetails: 'Sale Details',
        contactInfo: 'Contact Information',
        addressInfo: 'Address Information',
        editSale: 'Edit Sale',
        saveChanges: 'Save Changes',
        cancel: 'Cancel',
        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this sale?',
        deleteWarning: 'This action cannot be undone.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        close: 'Close',
        salesReport: 'Sales Report',
        salesDirectory: 'Sales Directory',
        totalSales: 'Total Sales',
        generated: 'Generated',
        reportId: 'Report ID',
        generatedBy: 'Generated by',
        shopCode: 'Shop Code',
        filtersApplied: 'FILTERS APPLIED',
        search: 'Search',
        from: 'From',
        to: 'To',
        page: 'Page',
        paid: 'Paid',
        unpaid: 'Unpaid',
        partiallyPaid: 'Partially Paid',
        items: 'Items',
        productName: 'Product',
        quantity: 'Qty',
        price: 'Price',
        total: 'Total',
        subtotal: 'Subtotal',
        tax: 'Tax',
        discount: 'Discount',
        updateSale: 'Update Sale',
        updating: 'Updating...',
        saleUpdated: 'Sale updated successfully',
        updateError: 'Error updating sale',
        cash: 'Cash',
        card: 'Card',
        upi: 'UPI',
        bankTransfer: 'Bank Transfer',
        customerAddress: 'Address',
        notes: 'Notes',
        saleId: 'Sale ID',
        itemBatch: 'Batch',
        itemUnit: 'Unit',
        allPages: 'All Pages',
        billRequired: 'Bill number is required',
        dateRequired: 'Date is required',
        deletedSuccess: 'Sale deleted successfully',
        deleteError: 'Could not delete sale',
        editableNotice: 'Edit bill details and line items. Totals update automatically from prices, quantities, tax, and discount.',
        networkError: 'Network error. Please try again.',
        noDataToExport: 'There is no sales data to export',
        optional: 'Optional',
        pageRange: 'Page Range',
        pages: 'Pages',
        perPage: 'Per Page',
        pdfGenerationError: 'Could not generate the PDF',
        poweredBy: 'Powered by OurZest',
        purchaseReceipt: 'Sales Invoice',
        reportGenerated: 'Report generated successfully',
        show: 'Show',
        supplier: 'Seller',
        thankYou: 'Thank you for your business',
        walkInCustomer: 'Walk-in Customer',
        hideFilters: 'Hide Filters',
        filters: 'Filters',
        billFrom: 'Bill From',
        billTo: 'Bill To',
        invoiceDetails: 'Invoice Details',
        invoiceNumber: 'Invoice No',
        payment: 'Payment',
        importantNote: 'Important Note',
        termsTitle: 'Terms & Conditions',
        termsText: 'Please verify products, quantities and amounts before leaving. This invoice should be retained for your purchase record and warranty claims, where applicable.',
        warranty: 'Warranty',
        shop: 'Shop',
        courier: 'Courier',
        trackingNumber: 'Tracking Number',
        packingStatus: 'Packing Status',
        unpacked: 'Unpacked',
        partiallyPacked: 'Partially Packed',
        packed: 'Packed',
        scannerReady: 'Scanner ready while this window is open',
        paymentLockedHint: 'Paid amount is preserved because this sale used customer credit or a previous-balance allocation',
        paymentLockedNote: 'Paid amount is preserved because this sale affected customer credit or a previous balance.',
        saleInformation: 'Sale Information',
        paymentSummary: 'Payment Summary',
        issuanceDate: 'Issuance Date',
        pageSummary: 'Page Summary'
    };

    // Urdu is deliberately bundled rather than fetched from a public translation
    // service. This keeps the screen and printed invoice complete and predictable,
    // including when the user is offline or the translation service rate-limits.
    const urduTexts = {
        salesList: 'فروخت کی فہرست',
        formDetails: 'فروخت کی تمام تفصیلات نیچے درج ہیں',
        sales: 'فروخت',
        shopName: 'دکان',
        noShopSelected: 'کوئی دکان منتخب نہیں۔ پہلے دکان منتخب کریں۔',
        switchShop: 'دکان تبدیل کریں',
        loadingShops: 'دکانیں لوڈ ہو رہی ہیں۔۔۔',
        noShops: 'کوئی دکان دستیاب نہیں',
        current: 'موجودہ',
        searchPlaceholder: 'فروخت تلاش کریں۔۔۔',
        refresh: 'تازہ کریں',
        loadingUserData: 'فروخت کا ڈیٹا لوڈ ہو رہا ہے۔۔۔',
        selectShop: 'فروخت دیکھنے کے لیے دکان منتخب کریں',
        billNumber: 'بل نمبر',
        bn: 'بی این',
        customerName: 'گاہک کا نام',
        phone: 'فون نمبر',
        date: 'تاریخ',
        grandTotal: 'مجموعی رقم',
        paidAmount: 'ادا شدہ رقم',
        balance: 'بقایا رقم',
        paymentMethod: 'ادائیگی کا طریقہ',
        paymentStatus: 'ادائیگی کی حیثیت',
        actions: 'کارروائیاں',
        addedBy: 'درج کرنے والا',
        edit: 'ترمیم',
        delete: 'حذف کریں',
        view: 'دیکھیں',
        print: 'پرنٹ کریں',
        serialNo: 'نمبر',
        showing: 'دکھائے جا رہے ہیں',
        of: 'میں سے',
        previous: 'پچھلا',
        next: 'اگلا',
        all: 'سب',
        billNumberFilter: 'بل نمبر',
        customerNameFilter: 'گاہک کا نام',
        phoneFilter: 'فون نمبر',
        addedByFilter: 'درج کرنے والا',
        dateRange: 'تاریخ کی حد',
        startDate: 'ابتدائی تاریخ',
        endDate: 'آخری تاریخ',
        paymentStatusFilter: 'ادائیگی کی حیثیت',
        filterBy: 'فلٹر کریں',
        exportToPDF: 'پی ڈی ایف بنائیں',
        orientation: 'صفحے کی سمت',
        portrait: 'عمودی',
        landscape: 'افقی',
        pageSize: 'صفحے کا سائز',
        a4: 'اے 4',
        letter: 'لیٹر',
        legal: 'لیگل',
        a5: 'اے 5',
        saleDetails: 'فروخت کی تفصیلات',
        contactInfo: 'رابطے کی معلومات',
        addressInfo: 'پتے کی معلومات',
        editSale: 'فروخت میں ترمیم',
        saveChanges: 'تبدیلیاں محفوظ کریں',
        cancel: 'منسوخ کریں',
        confirmDelete: 'حذف کرنے کی تصدیق',
        deleteMessage: 'کیا آپ واقعی یہ فروخت حذف کرنا چاہتے ہیں؟',
        deleteWarning: 'یہ عمل واپس نہیں ہو سکتا۔',
        yesDelete: 'ہاں، حذف کریں',
        noCancel: 'نہیں، منسوخ کریں',
        success: 'کامیابی',
        error: 'خرابی',
        warning: 'انتباہ',
        close: 'بند کریں',
        salesReport: 'فروخت کی رپورٹ',
        salesDirectory: 'فروخت کی تفصیلی فہرست',
        totalSales: 'کل فروخت',
        generated: 'تیار کردہ',
        reportId: 'رپورٹ نمبر',
        generatedBy: 'تیار کرنے والا',
        shopCode: 'دکان کا کوڈ',
        filtersApplied: 'لاگو شدہ فلٹرز',
        search: 'تلاش',
        from: 'سے',
        to: 'تک',
        page: 'صفحہ',
        paid: 'ادا شدہ',
        unpaid: 'غیر ادا شدہ',
        partiallyPaid: 'جزوی ادا شدہ',
        items: 'اشیاء',
        productName: 'مصنوعہ',
        quantity: 'مقدار',
        price: 'قیمت',
        total: 'کل رقم',
        subtotal: 'ذیلی مجموعہ',
        tax: 'ٹیکس',
        discount: 'رعایت',
        updateSale: 'فروخت اپ ڈیٹ کریں',
        updating: 'اپ ڈیٹ ہو رہا ہے۔۔۔',
        saleUpdated: 'فروخت کامیابی سے اپ ڈیٹ ہو گئی',
        updateError: 'فروخت اپ ڈیٹ نہیں ہو سکی',
        cash: 'نقد',
        card: 'کارڈ',
        upi: 'یو پی آئی',
        bankTransfer: 'بینک منتقلی',
        customerAddress: 'پتہ',
        notes: 'نوٹس',
        saleId: 'فروخت نمبر',
        itemBatch: 'بیچ',
        itemUnit: 'اکائی',
        allPages: 'تمام صفحات',
        billRequired: 'بل نمبر درج کرنا ضروری ہے',
        dateRequired: 'تاریخ درج کرنا ضروری ہے',
        deletedSuccess: 'فروخت کامیابی سے حذف ہو گئی',
        deleteError: 'فروخت حذف نہیں ہو سکی',
        editableNotice: 'بل اور اشیاء کی تفصیل میں ترمیم کریں۔ قیمت، مقدار، ٹیکس اور رعایت کے مطابق حساب خود بخود بدل جائے گا۔',
        networkError: 'نیٹ ورک کی خرابی۔ دوبارہ کوشش کریں۔',
        noDataToExport: 'ایکسپورٹ کرنے کے لیے فروخت کا ڈیٹا موجود نہیں',
        optional: 'اختیاری',
        pageRange: 'صفحات کی حد',
        pages: 'صفحات',
        perPage: 'فی صفحہ',
        pdfGenerationError: 'پی ڈی ایف تیار نہیں ہو سکی',
        poweredBy: 'آور زیسٹ کی پیشکش',
        purchaseReceipt: 'فروخت کی رسید',
        reportGenerated: 'رپورٹ کامیابی سے تیار ہو گئی',
        show: 'دکھائیں',
        supplier: 'فروخت کنندہ',
        thankYou: 'آپ کی خریداری کا شکریہ',
        walkInCustomer: 'عام گاہک',
        hideFilters: 'فلٹر چھپائیں',
        filters: 'فلٹرز',
        billFrom: 'بل جاری کنندہ',
        billTo: 'بل بنام',
        invoiceDetails: 'انوائس کی تفصیلات',
        invoiceNumber: 'انوائس نمبر',
        payment: 'ادائیگی',
        importantNote: 'اہم نوٹ',
        termsTitle: 'شرائط و ضوابط',
        termsText: 'براہ کرم جانے سے پہلے مصنوعات، مقدار اور رقم کی تصدیق کر لیں۔ خریداری کے ریکارڈ اور قابلِ اطلاق وارنٹی دعوے کے لیے یہ رسید محفوظ رکھیں۔',
        warranty: 'وارنٹی',
        shop: 'دکان',
        courier: 'کوریئر',
        trackingNumber: 'ٹریکنگ نمبر',
        packingStatus: 'پیکنگ کی حالت',
        unpacked: 'غیر پیک شدہ',
        partiallyPacked: 'جزوی پیک شدہ',
        packed: 'پیک شدہ',
        scannerReady: 'یہ ونڈو کھلی رہنے تک اسکینر تیار ہے',
        paymentLockedHint: 'چونکہ اس فروخت میں گاہک کا کریڈٹ یا سابقہ بقایا استعمال ہوا، اس لیے ادا شدہ رقم محفوظ رکھی گئی ہے',
        paymentLockedNote: 'چونکہ اس فروخت نے گاہک کے کریڈٹ یا سابقہ بقایا کو متاثر کیا، اس لیے ادا شدہ رقم تبدیل نہیں کی جا سکتی۔',
        saleInformation: 'فروخت کی معلومات',
        paymentSummary: 'ادائیگی کا خلاصہ',
        issuanceDate: 'اجراء کی تاریخ',
        pageSummary: 'صفحے کا خلاصہ'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        if (lang === 'ur') {
            return { ...defaultTexts, ...urduTexts };
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const salesListTranslator = createSalesListTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations.success || 'Success';
            case 'error': return translations.error || 'Error';
            case 'warning': return translations.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};


// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading, darkMode }) => {
    if (!isOpen) return null;

    return (
        <div className={`ozlistui-modal-overlay ${darkMode ? 'ozlistui-dark' : ''}`} onClick={onClose}>
            <div className="ozlistui-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="ozlistui-modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="ozlistui-close-btn" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="ozlistui-modal-body">
                    {loading ? (
                        <div className="ozlistui-loading-shops">
                            <div className="ozlistui-loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="ozlistui-no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="ozlistui-shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`ozlistui-shop-item ${currentShop?.shop_id === shop.shop_id ? 'ozlistui-active' : ''}`}
                                    onClick={() => onSelectShop(shop)}
                                >
                                    <div className="ozlistui-shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="ozlistui-shop-item-details">
                                        <div className="ozlistui-shop-item-name">{shop.shop_name}</div>
                                        <div className="ozlistui-shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="ozlistui-shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Custom Dropdown Component
const CustomDropdown = ({ label, icon, value, onChange, options, placeholder, translations, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [isHovering, setIsHovering] = useState(false);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const menuRef = useRef(null);
    const hoverTimeoutRef = useRef(null);

    const selectedOption = options.find(opt => opt.value === value);
    const displayValue = selectedOption?.label || '';

    // Sync search term with selected value ONLY when not hovering
    useEffect(() => {
        if (!isHovering) {
            setSearchTerm(displayValue);
        }
    }, [displayValue, isHovering]);

    // Close all other dropdowns when this one opens
    useEffect(() => {
        if (isOpen) {
            const event = new CustomEvent('dropdownOpened', { detail: { id: label } });
            window.dispatchEvent(event);
        }
    }, [isOpen, label]);

    // Listen for dropdown opened events from other dropdowns
    useEffect(() => {
        const handleDropdownOpened = (event) => {
            if (event.detail && event.detail.id !== label) {
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
            }
        };
        window.addEventListener('dropdownOpened', handleDropdownOpened);
        return () => window.removeEventListener('dropdownOpened', handleDropdownOpened);
    }, [label]);

    // Filter options - SHOW ALL when hovering, otherwise filter by search term
    const filteredOptions = isHovering
        ? options // Show ALL options when hovering
        : searchTerm.trim() === ''
            ? options
            : options.filter(opt =>
                opt.label.toLowerCase().includes(searchTerm.toLowerCase())
            );

    const handleSelect = (option) => {
        onChange(option.value);
        setSearchTerm(option.label);
        setIsOpen(false);
        setHighlightedIndex(-1);
        setIsHovering(false);
    };

    // Keyboard navigation
    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(true);
                setHighlightedIndex(0);
                setIsHovering(false);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < filteredOptions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                    handleSelect(filteredOptions[highlightedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
                break;
            case 'Tab':
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
                break;
            default:
                break;
        }
    };

    // Hover to open - shows ALL options
    const handleMouseEnter = () => {
        if (disabled) return;
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        hoverTimeoutRef.current = setTimeout(() => {
            setIsHovering(true);
            setIsOpen(true);
            setHighlightedIndex(0);
        }, 200);
    };

    const handleMouseLeave = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        // Don't close immediately - let click outside handle it
        // But reset hovering state after a delay
        setTimeout(() => {
            if (!isOpen) {
                setIsHovering(false);
            }
        }, 300);
    };

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Scroll highlighted item into view
    useEffect(() => {
        if (highlightedIndex >= 0 && menuRef.current) {
            const items = menuRef.current.querySelectorAll('.ozlistui-custom-dropdown-option');
            if (items[highlightedIndex]) {
                items[highlightedIndex].scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex]);

    // Handle input click
    const handleInputClick = () => {
        if (disabled) return;
        setIsHovering(false);
        setSearchTerm(displayValue);
        setIsOpen(true);
        setHighlightedIndex(0);
    };

    // Handle input change (typing)
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setIsHovering(false);
        setSearchTerm(newValue);
        setIsOpen(true);
        setHighlightedIndex(0);
        if (!options.find(opt => opt.label.toLowerCase() === newValue.toLowerCase())) {
            onChange('');
        }
    };

    return (
        <div
            className="ozlistui-custom-dropdown-wrapper"
            ref={wrapperRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="ozlistui-custom-input-wrapper">
                <input
                    ref={inputRef}
                    type="text"
                    value={searchTerm}
                    onChange={handleInputChange}
                    onClick={handleInputClick}
                    onFocus={() => {
                        if (!disabled) {
                            setIsHovering(false);
                            setSearchTerm(displayValue);
                            setIsOpen(true);
                            setHighlightedIndex(0);
                        }
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder=" "
                    disabled={disabled}
                    autoComplete="off"
                />
                <label>
                    {icon && <FontAwesomeIcon icon={icon} />}
                    {label}
                </label>
                <FontAwesomeIcon icon={faChevronDown} className="ozlistui-custom-dropdown-arrow" />
            </div>

            {isOpen && (
                <div className="ozlistui-custom-dropdown-menu" ref={menuRef}>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((opt, index) => (
                            <div
                                key={opt.value}
                                className={`ozlistui-custom-dropdown-option ${value === opt.value ? 'ozlistui-selected' : ''} ${highlightedIndex === index ? 'ozlistui-highlighted' : ''}`}
                                onClick={() => handleSelect(opt)}
                                onMouseEnter={() => setHighlightedIndex(index)}
                            >
                                {opt.label}
                            </div>
                        ))
                    ) : (
                        <div className="ozlistui-custom-dropdown-empty">
                            No options found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Main Component
const SolarSpecialSalesList = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [sales, setSales] = useState([]);
    const [filteredSales, setFilteredSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSale, setSelectedSale] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [saleToDelete, setSaleToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    // PDF Options State
    const [pdfOrientation, setPdfOrientation] = useState('landscape');
    const [pdfPageSize, setPdfPageSize] = useState('a4');
    const [pdfPageRange, setPdfPageRange] = useState('all');
    const [pdfStartPage, setPdfStartPage] = useState(1);
    const [pdfEndPage, setPdfEndPage] = useState(1);

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Modal drag and resize state
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [modalSize, setModalSize] = useState({ width: 650, height: 'auto' });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [currentWidthMode, setCurrentWidthMode] = useState('wide');
    const [isModalInitialized, setIsModalInitialized] = useState(false);

    const dragStart = useRef({ x: 0, y: 0 });
    const modalRef = useRef(null);
    const resizeType = useRef(null);
    const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });

    const widthModes = {
        narrow: { width: 400, label: 'Narrow', icon: faMinus },
        medium: { width: 650, label: 'Medium', icon: faArrowsAlt },
        wide: { width: 850, label: 'Wide', icon: faPlus },
        full: { width: '95vw', label: 'Full Width', icon: faExpand },
        fullscreen: { width: '98vw', label: 'Full Screen', icon: faCompress }
    };

    // Theme, Dark Mode, Language State
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [translations, setTranslations] = useState({});

    const changeLanguage = useCallback((language) => {
        const nextLanguage = language === 'ur' ? 'ur' : 'en';
        localStorage.setItem('ourzest_language', nextLanguage);
        setCurrentLanguage(nextLanguage);
        window.dispatchEvent(new CustomEvent('languageChanged', {
            detail: { language: nextLanguage }
        }));
    }, []);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        progress: 100,
        timeLeft: 3
    });

    const toastTimerRef = useRef(null);
    const isMountedRef = useRef(true);

    // Edit form state
    const [editFormData, setEditFormData] = useState({
        id: null,
        date: '',
        bill_number: '',
        bn: '',
        customer_name: '',
        customer_phone: '',
        customer_address: '',
        subtotal: '',
        tax: '',
        discount: '',
        grand_total: '',
        paid_amount: '',
        received_amount: '',
        payment_locked: false,
        balance: '',
        payment_method: 'cash',
        payment_status: 'unpaid',
        notes: '',
        courier_name: '',
        tracking_number: '',
        packing_status: 'unpacked',
        items: []
    });
    const [editFormErrors, setEditFormErrors] = useState({});

    // Business info state
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        businessaddress: '',
        phonenumber: '',
        fullname: '',
        email: '',
        shop_name: '',
        shop_code: '',
        cnotes: '',
        cdesign: null,
        cdesign_picture: '',
        cdesign_picture_url: ''
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(100);

    // Filter states
    const [filterBy, setFilterBy] = useState('');
    const [billNumberFilter, setBillNumberFilter] = useState('');
    const [customerNameFilter, setCustomerNameFilter] = useState('');
    const [phoneFilter, setPhoneFilter] = useState('');
    const [addedByFilter, setAddedByFilter] = useState('');
    const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const escapeHtml = (text) => {
        if (!text) return '';
        return String(text).replace(/[&<>]/g, function (m) {
            if (m === '&') return '&amp;';
            if (m === '<') return '&lt;';
            if (m === '>') return '&gt;';
            return m;
        });
    };

    const paymentMethodOptions = useMemo(() => [
        { value: '', label: 'All Methods' },
        { value: 'cash', label: translations.cash || 'Cash' },
        { value: 'card', label: translations.card || 'Card' },
        { value: 'upi', label: translations.upi || 'UPI' },
        { value: 'bank_transfer', label: translations.bankTransfer || 'Bank Transfer' }
    ], [translations]);

    const paymentStatusOptions = useMemo(() => [
        { value: '', label: translations.all || 'All' },
        { value: 'paid', label: translations.paid || 'Paid' },
        { value: 'unpaid', label: translations.unpaid || 'Unpaid' },
        { value: 'partial', label: translations.partiallyPaid || 'Partially Paid' }
    ], [translations]);

    const filterByOptions = useMemo(() => [
        { value: 'all', label: translations.all || 'All' },
        { value: 'billNumber', label: translations.billNumberFilter || 'Bill Number' },
        { value: 'customerName', label: translations.customerNameFilter || 'Customer Name' },
        { value: 'phone', label: translations.phoneFilter || 'Phone Number' },
        { value: 'added_by', label: translations.addedByFilter || 'Added By' },
        { value: 'date', label: translations.dateRange || 'Date Range' },
        { value: 'paymentStatus', label: translations.paymentStatusFilter || 'Payment Status' }
    ], [translations]);

    const pdfOrientationOptions = useMemo(() => [
        { value: 'portrait', label: translations.portrait || 'Portrait' },
        { value: 'landscape', label: translations.landscape || 'Landscape' }
    ], [translations]);

    const pdfPageSizeOptions = useMemo(() => [
        { value: 'a4', label: translations.a4 || 'A4' },
        { value: 'letter', label: translations.letter || 'Letter' },
        { value: 'legal', label: translations.legal || 'Legal' },
        { value: 'a5', label: translations.a5 || 'A5' }
    ], [translations]);

    // ADD MISSING pdfPageRangeOptions HERE
    const pdfPageRangeOptions = useMemo(() => [
        { value: 'all', label: translations.allPages || 'All Pages' },
        { value: 'range', label: translations.pageRange || 'Page Range' }
    ], [translations]);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        setToast({ show: true, type, message, progress: 100, timeLeft: 3 });
        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;
            setToast(prev => ({ ...prev, progress, timeLeft: parseFloat(timeLeft.toFixed(1)) }));
            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);
        toastTimerRef.current = timer;
    }, []);

    const recalculateEditSale = useCallback((items, taxValue, discountValue, paidValue) => {
        const subtotal = items.reduce((sum, item) => sum + ((Number(item.price) || 0) * (Number(item.quantity) || 0)), 0);
        const tax = Number(taxValue) || 0;
        const discount = Number(discountValue) || 0;
        const paid = Number(paidValue) || 0;
        const grandTotal = Math.max(0, subtotal + tax - discount);
        const balance = grandTotal - paid;
        return {
            subtotal: subtotal.toFixed(2), grand_total: grandTotal.toFixed(2), balance: balance.toFixed(2),
            payment_status: balance <= 0 ? 'paid' : (paid > 0 ? 'partial' : 'unpaid')
        };
    }, []);

    const searchEditSaleProducts = useCallback(async (term) => {
        if (!term || term.trim().length < 2 || !user?.username || !currentShop?.shop_id) return [];
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'search_products', searchTerm: term.trim(), username: user.username, shop_id: currentShop.shop_id })
            });
            const result = await response.json();
            return result.status === 'success' ? (result.data || []) : [];
        } catch (error) { return []; }
    }, [user?.username, currentShop?.shop_id]);

    const updateEditSaleItem = useCallback((index, field, value) => {
        setEditFormData(prev => {
            const items = prev.items.map((item, itemIndex) => {
                if (itemIndex !== index) return item;
                const next = { ...item, [field]: value };
                if (field === 'issue_warranty') {
                    const availableWarranty = String(item.product_warranty || item.warranty || '').trim();
                    next.issue_warranty = Boolean(value && availableWarranty);
                    next.WarrantyStatus = next.issue_warranty ? 1 : 0;
                    next.warranty_issuance_date = next.issue_warranty
                        ? (item.warranty_issuance_date || prev.date || new Date().toISOString().split('T')[0])
                        : '';
                }
                next.total = field === 'price' || field === 'quantity'
                    ? String(Math.round((Number(field === 'price' ? value : item.price) || 0) * (Number(field === 'quantity' ? value : item.quantity) || 0)))
                    : item.total;
                return next;
            });
            return { ...prev, items, ...recalculateEditSale(items, prev.tax, prev.discount, prev.paid_amount) };
        });
    }, [recalculateEditSale]);

    const selectEditSaleProduct = useCallback((index, product) => {
        setEditFormData(prev => {
            const items = [...prev.items];
            items[index] = { ...items[index], ...buildSaleItem(product, currentShop?.shop_id), quantity: items[index]?.quantity || '1' };
            return { ...prev, items, ...recalculateEditSale(items, prev.tax, prev.discount, prev.paid_amount) };
        });
    }, [currentShop?.shop_id, recalculateEditSale]);

    const addEditSaleItem = useCallback(() => setEditFormData(prev => ({
        ...prev, items: [...prev.items, {
            product_id: '', product_name: '', product_code: '', batch_no: '', barcode: '', warranty: '', product_warranty: '', issue_warranty: false, WarrantyStatus: 0, warranty_issuance_date: '', price: '', quantity: '1',
            available_stock: 0, total: '0.00', sell_method: 'sub_packing', items_per_pack: 1
        }]
    })), []);

    const removeEditSaleItem = useCallback((index) => setEditFormData(prev => {
        const items = prev.items.filter((_, itemIndex) => itemIndex !== index);
        return { ...prev, items, ...recalculateEditSale(items, prev.tax, prev.discount, prev.paid_amount) };
    }), [recalculateEditSale]);

    const handleEditSaleBarcode = useCallback(async (barcode) => {
        if (!showEditModal) return;
        const matches = await searchEditSaleProducts(barcode);
        const product = matches.find(item => String(item.barcode) === String(barcode)) || matches[0];
        if (!product) return showToast('warning', `Product not found: ${barcode}`);
        setEditFormData(prev => {
            const found = prev.items.findIndex(item => String(item.product_id) === String(product.id));
            let items;
            if (found >= 0) items = prev.items.map((item, index) => index === found ? { ...item, quantity: String((Number(item.quantity) || 0) + 1) } : item);
            else items = [...prev.items, buildSaleItem(product, currentShop?.shop_id)];
            return { ...prev, items, ...recalculateEditSale(items, prev.tax, prev.discount, prev.paid_amount) };
        });
    }, [showEditModal, searchEditSaleProducts, currentShop?.shop_id, recalculateEditSale, showToast]);

    useBarcodeScanner(handleEditSaleBarcode, showEditModal && !editLoading);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        };
    }, []);

    // ============================================
    // THEME SYNC
    // ============================================
    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
            color = '#6366f1';
        }
        document.documentElement.style.setProperty('--ozlistui-primary', color);
        document.documentElement.style.setProperty('--ozlistui-primary-dark', color);
        document.documentElement.style.setProperty('--ozlistui-primary-light', color);
        setCurrentTheme(color);

        // Update dynamic styles
        let style = document.getElementById('dynamic-list-styles');
        if (!style) {
            style = document.createElement('style');
            style.id = 'dynamic-list-styles';
            document.head.appendChild(style);
        }

        style.textContent = `
            .ozlistui-table thead th {
                color: ${color};
                border-bottom-color: ${color};
            }
            .ozlistui-payment-badge {
                border-color: ${color}40;
            }
            .ozlistui-page-number.ozlistui-active {
                background: ${color};
                border-color: ${color};
            }
            .ozlistui-action-btn.ozlistui-edit-btn:hover:not(:disabled) {
                background: ${color};
            }
            .ozlistui-header-shop-btn,
            .ozlistui-header-filter-btn {
                background: linear-gradient(135deg, ${color}, ${adjustColor(color, -20)});
            }
            .ozlistui-btn-primary {
                background: ${color};
            }
            .ozlistui-custom-dropdown-option.ozlistui-selected {
                background: ${color};
            }
        `;
    }, []);

    useEffect(() => {
        const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(savedTheme);

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                const newTheme = e.newValue || '#6366f1';
                applyTheme(newTheme);
            }
        };

        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);

        const interval = setInterval(() => {
            const storedTheme = localStorage.getItem('ourzest_theme');
            if (storedTheme && storedTheme !== currentTheme) {
                applyTheme(storedTheme);
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
            clearInterval(interval);
        };
    }, [applyTheme, currentTheme]);

    // ============================================
    // DARK MODE SYNC
    // ============================================
    const syncDarkMode = useCallback(() => {
        const isDark = localStorage.getItem('ourzest_darkMode') === 'true';
        setDarkMode(isDark);

        const container = document.querySelector('.ozlistui-container');
        if (container) {
            if (isDark) {
                container.classList.add('ozlistui-dark');
            } else {
                container.classList.remove('ozlistui-dark');
            }
        }

        // Also update modal overlays
        const overlays = document.querySelectorAll('.ozlistui-modal-overlay');
        overlays.forEach(overlay => {
            if (isDark) {
                overlay.classList.add('ozlistui-dark');
            } else {
                overlay.classList.remove('ozlistui-dark');
            }
        });

        if (isDark) {
            document.body.classList.add('ozlistui-dark');
            document.documentElement.classList.add('ozlistui-dark');
        } else {
            document.body.classList.remove('ozlistui-dark');
            document.documentElement.classList.remove('ozlistui-dark');
        }
    }, []);

    useEffect(() => {
        syncDarkMode();

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };

        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);
                const container = document.querySelector('.ozlistui-container');
                if (container) {
                    if (isDark) {
                        container.classList.add('ozlistui-dark');
                    } else {
                        container.classList.remove('ozlistui-dark');
                    }
                }
                const overlays = document.querySelectorAll('.ozlistui-modal-overlay');
                overlays.forEach(overlay => {
                    if (isDark) {
                        overlay.classList.add('ozlistui-dark');
                    } else {
                        overlay.classList.remove('ozlistui-dark');
                    }
                });
                if (isDark) {
                    document.body.classList.add('ozlistui-dark');
                    document.documentElement.classList.add('ozlistui-dark');
                } else {
                    document.body.classList.remove('ozlistui-dark');
                    document.documentElement.classList.remove('ozlistui-dark');
                }
            } else {
                syncDarkMode();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);

        const interval = setInterval(() => {
            const storedDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            if (storedDarkMode !== darkMode) {
                syncDarkMode();
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
            clearInterval(interval);
        };
    }, [syncDarkMode, darkMode]);

    // ============================================
    // LANGUAGE SYNC
    // ============================================
    const loadTranslations = useCallback(async (lang) => {
        const trans = await salesListTranslator.getTranslations(lang);
        setTranslations(trans);
    }, []);

    useEffect(() => {
        loadTranslations(currentLanguage);

        const handleStorageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);

        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage, loadTranslations]);

    // ============================================
    // RTL DIRECTION SYNC
    // ============================================
    useEffect(() => {
        const isRTL = RTL_LANGUAGE_CODES.has(currentLanguage);
        const container = document.querySelector('.ozlistui-container');
        const previousHtmlDir = document.documentElement.getAttribute('dir');
        const previousBodyDir = document.body.getAttribute('dir');

        if (container) {
            if (isRTL) {
                container.classList.add('ozlistui-rtl');
                container.classList.remove('ozlistui-ltr');
            } else {
                container.classList.add('ozlistui-ltr');
                container.classList.remove('ozlistui-rtl');
            }
        }

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }

        return () => {
            if (previousHtmlDir === null) document.documentElement.removeAttribute('dir');
            else document.documentElement.setAttribute('dir', previousHtmlDir);

            if (previousBodyDir === null) document.body.removeAttribute('dir');
            else document.body.setAttribute('dir', previousBodyDir);
        };
    }, [currentLanguage]);

    // Center modal function
    const centerModal = useCallback(() => {
        if (!modalRef.current) return;
        const rect = modalRef.current.getBoundingClientRect();
        const left = (window.innerWidth - rect.width) / 2;
        const top = (window.innerHeight - rect.height) / 2;
        modalRef.current.style.left = `${Math.max(0, left)}px`;
        modalRef.current.style.top = `${Math.max(0, top)}px`;
        modalRef.current.style.transform = 'none';
        setModalPosition({ x: Math.max(0, left), y: Math.max(0, top) });
    }, []);

    const setWidthMode = useCallback((mode) => {
        if (!modalRef.current) return;
        setCurrentWidthMode(mode);
        const widthValue = widthModes[mode].width;
        if (mode === 'fullscreen') {
            modalRef.current.style.width = '98vw';
            modalRef.current.style.height = '90vh';
        } else {
            modalRef.current.style.width = typeof widthValue === 'number' ? `${widthValue}px` : widthValue;
            modalRef.current.style.height = 'auto';
        }
        setModalSize({ width: modalRef.current.offsetWidth, height: modalRef.current.offsetHeight });
        setTimeout(() => centerModal(), 10);
    }, [widthModes, centerModal]);

    const startDrag = useCallback((e) => {
        if (!modalRef.current) return;
        if (e.target.closest('button, input, select, textarea, a, .ozlistui-width-btn, .ozlistui-close-btn')) return;
        e.preventDefault();
        setIsDragging(true);
        const rect = modalRef.current.getBoundingClientRect();
        dragStart.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        modalRef.current.style.transition = 'none';
    }, []);

    const startResize = useCallback((e, type) => {
        e.preventDefault();
        e.stopPropagation();
        if (!modalRef.current) return;
        setIsResizing(true);
        resizeType.current = type;
        const rect = modalRef.current.getBoundingClientRect();
        resizeStart.current = {
            x: e.clientX, y: e.clientY,
            width: rect.width, height: rect.height,
            left: rect.left, top: rect.top,
            minWidth: 350, minHeight: 400,
            maxWidth: window.innerWidth - 40, maxHeight: window.innerHeight - 40
        };
        modalRef.current.style.transition = 'none';
    }, []);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging && modalRef.current) {
                let newLeft = e.clientX - dragStart.current.x;
                let newTop = e.clientY - dragStart.current.y;
                const rect = modalRef.current.getBoundingClientRect();
                newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - rect.width));
                newTop = Math.max(0, Math.min(newTop, window.innerHeight - rect.height));
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                setModalPosition({ x: newLeft, y: newTop });
            }

            if (isResizing && modalRef.current && resizeType.current) {
                let newWidth = resizeStart.current.width;
                let newHeight = resizeStart.current.height;
                let newLeft = resizeStart.current.left;
                let newTop = resizeStart.current.top;
                const deltaX = e.clientX - resizeStart.current.x;
                const deltaY = e.clientY - resizeStart.current.y;

                switch (resizeType.current) {
                    case 'e': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); break;
                    case 'w': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); break;
                    case 's': newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); break;
                    case 'n': newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'ne': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'nw': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'se': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); break;
                    case 'sw': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); break;
                }
                modalRef.current.style.width = `${newWidth}px`;
                modalRef.current.style.height = `${newHeight}px`;
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalSize({ width: newWidth, height: newHeight });
                setModalPosition({ x: newLeft, y: newTop });
            }
        };

        const handleMouseUp = () => {
            if (isDragging || isResizing) {
                setIsDragging(false);
                setIsResizing(false);
                resizeType.current = null;
                if (modalRef.current) modalRef.current.style.transition = '';
            }
        };

        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, isResizing]);

    useEffect(() => {
        if (showEditModal && modalRef.current && !isModalInitialized) {
            setTimeout(() => { centerModal(); setWidthMode('wide'); setIsModalInitialized(true); }, 50);
        }
        if (!showEditModal) setIsModalInitialized(false);
    }, [showEditModal, centerModal, setWidthMode, isModalInitialized]);

    useEffect(() => {
        const handleWindowResize = () => {
            if (!modalRef.current || !showEditModal) return;
            const rect = modalRef.current.getBoundingClientRect();
            let newLeft = Math.min(rect.left, window.innerWidth - rect.width);
            let newTop = Math.min(rect.top, window.innerHeight - rect.height);
            newLeft = Math.max(0, newLeft);
            newTop = Math.max(0, newTop);
            if (newLeft !== rect.left || newTop !== rect.top) {
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [showEditModal]);

    // Fetch sales
    const fetchSales = useCallback(async () => {
        if (!user || !user.username) {
            if (isMountedRef.current) {
                showToast('error', 'User information not available');
                setLoading(false);
            }
            return;
        }
        if (!currentShop) {
            if (isMountedRef.current) {
                showToast('error', translations.noShopSelected || 'Please select a shop first');
                setLoading(false);
            }
            return;
        }
        if (isMountedRef.current) setLoading(true);
        try {
            const response = await fetch(SELL_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    language: currentLanguage,
                    filters: {}
                })
            });
            const data = await response.json();
            if (isMountedRef.current) {
                if (data.status === 'success') {
                    const sourceRows = data.data || [];
                    setSales(sourceRows);
                    setFilteredSales(sourceRows);
                    resetFilters();
                    const translatedRows = await translateSavedRows(
                        sourceRows,
                        ['customer_name', 'customer_address', 'notes', 'courier_name'],
                        currentLanguage,
                        { nested: [{ key: 'items', fields: ['product_name', 'model', 'color', 'size', 'class', 'supplier_name', 'company'] }] }
                    );
                    if (!isMountedRef.current) return;
                    setSales(translatedRows);
                    setFilteredSales(translatedRows);
                } else {
                    showToast('error', data.message || translations.networkError || 'Network error occurred');
                }
            }
        } catch (error) {
            console.error('Error fetching sales:', error);
            if (isMountedRef.current) showToast('error', translations.networkError || 'Network error occurred');
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    }, [user, currentShop, currentLanguage, translations, showToast]);

    const fetchAvailableShops = useCallback(async () => {
        if (!user) return;
        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            if (isMountedRef.current) setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            if (isMountedRef.current) setAvailableShops([]);
        } finally {
            if (isMountedRef.current) setLoadingShops(false);
        }
    }, [user, getShopsForSwitch]);

    const fetchBusinessInfo = useCallback(async () => {
        if (!user || !user.username) return;
        try {
            const data = await getBusinessInfoOnce(user.username);
            if (isMountedRef.current) {
                // Check if we got valid data
                if (data && data.id) {
                    setBusinessInfo({
                        businessname: data.businessname || '',
                        bussinesslogo: data.bussinesslogo || '',
                        bussinesslogo_url: data.bussinesslogo || '',
                        businessaddress: data.businessaddress || '',
                        phonenumber: data.phonenumber || '',
                        email: data.email || '',
                        fullname: data.fullname || '',
                        shop_name: data.shop_name || '',
                        shop_code: data.shop_code || '',
                        shop_id: data.shop_id || '',
                        default_shop_id: data.default_shop_id || '',
                        role: data.role || '',
                        all_shops: data.all_shops || [],
                        cnotes: data.cnotes || '',
                        cdesign: Number(data.cdesign) === 1 ? 1 : 0,
                        cdesign_picture: data.cdesign_picture || '',
                        cdesign_picture_url: data.cdesign_picture_url || data.cdesign_picture || '',
                        cdesign_source: data.cdesign_source || 'current_user',
                        cdesign_source_user_id: data.cdesign_source_user_id || data.id || null
                    });

                    // Warm the browser cache while the user views the sales list.
                    if (Number(data.cdesign) === 1 && data.cdesign_picture_url) {
                        const designPreloader = new Image();
                        designPreloader.decoding = 'async';
                        designPreloader.onerror = () => console.error('Receipt design image could not be loaded:', data.cdesign_picture_url);
                        designPreloader.src = data.cdesign_picture_url;
                    }
                } else {
                    // Fallback to user data
                    setBusinessInfo({
                        businessname: user.business_name || user.fullname || '',
                        bussinesslogo: '',
                        bussinesslogo_url: '',
                        businessaddress: user.business_address || '',
                        phonenumber: user.phonenumber || '',
                        email: user.email || '',
                        fullname: user.fullname || '',
                        shop_name: currentShop?.shop_name || '',
                        shop_code: currentShop?.shop_code || '',
                        shop_id: currentShop?.shop_id || '',
                        default_shop_id: currentShop?.shop_id || '',
                        role: user.role || '',
                        all_shops: [],
                        cnotes: user.cnotes || '',
                        cdesign: Number(user.cdesign) === 1 ? 1 : 0,
                        cdesign_picture: user.cdesign_picture || '',
                        cdesign_picture_url: user.cdesign_picture_url || ''
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
            if (isMountedRef.current) {
                setBusinessInfo({
                    businessname: user.business_name || user.fullname || '',
                    bussinesslogo: '',
                    bussinesslogo_url: '',
                    businessaddress: user.business_address || '',
                    phonenumber: user.phonenumber || '',
                    email: user.email || '',
                    fullname: user.fullname || '',
                    shop_name: currentShop?.shop_name || '',
                    shop_code: currentShop?.shop_code || '',
                    shop_id: currentShop?.shop_id || '',
                    default_shop_id: currentShop?.shop_id || '',
                    role: user.role || '',
                    all_shops: [],
                    cnotes: user.cnotes || '',
                    cdesign: Number(user.cdesign) === 1 ? 1 : 0,
                    cdesign_picture: user.cdesign_picture || '',
                    cdesign_picture_url: user.cdesign_picture_url || ''
                });
            }
        }
    }, [user, currentShop]);

    useEffect(() => {
        if (user && user.username && currentShop) {
            fetchSales();

            fetchBusinessInfo();
            fetchAvailableShops();
        } else if (user && user.username && !currentShop) {
            setLoading(false);
        } else if (!user) {
            setLoading(false);
        }
    }, [user, currentShop?.shop_id, currentLanguage]);

    const handleSwitchShop = useCallback(async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', 'Shop switched successfully');
                setCurrentPage(1);
                resetFilters();
                setShowShopSelector(false);
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', 'Failed to switch shop');
        }
    }, [switchShop, showToast]);

    const applyFilters = useCallback(() => {
        let filtered = [...sales];
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(sale =>
                sale.bill_number?.toLowerCase().includes(term) ||
                sale.bn?.toLowerCase().includes(term) ||
                sale.customer_name?.toLowerCase().includes(term) ||
                sale.customer_phone?.toLowerCase().includes(term) ||
                sale.username?.toLowerCase().includes(term)
            );
        }
        if (filterBy === 'billNumber' && billNumberFilter.trim()) {
            const term = billNumberFilter.toLowerCase();
            filtered = filtered.filter(sale => sale.bill_number?.toLowerCase().includes(term));
        }
        if (filterBy === 'customerName' && customerNameFilter.trim()) {
            const term = customerNameFilter.toLowerCase();
            filtered = filtered.filter(sale => sale.customer_name?.toLowerCase().includes(term));
        }
        if (filterBy === 'phone' && phoneFilter.trim()) {
            const term = phoneFilter.toLowerCase();
            filtered = filtered.filter(sale => sale.customer_phone?.toLowerCase().includes(term));
        }
        if (filterBy === 'added_by' && addedByFilter.trim()) {
            const term = addedByFilter.toLowerCase();
            filtered = filtered.filter(sale => sale.username?.toLowerCase().includes(term));
        }
        if (filterBy === 'paymentStatus' && paymentStatusFilter) {
            if (paymentStatusFilter === 'paid') {
                filtered = filtered.filter(sale => {
                    const balance = parseFloat(sale.balance) || 0;
                    return balance <= 0;
                });
            } else if (paymentStatusFilter === 'unpaid') {
                filtered = filtered.filter(sale => {
                    const paid = parseFloat(sale.paid_amount) || 0;
                    return paid === 0;
                });
            } else if (paymentStatusFilter === 'partial') {
                filtered = filtered.filter(sale => {
                    const paid = parseFloat(sale.paid_amount) || 0;
                    const balance = parseFloat(sale.balance) || 0;
                    return paid > 0 && balance > 0;
                });
            }
        }
        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(sale => {
                if (!sale.date) return false;
                const saleDate = new Date(sale.date);
                saleDate.setHours(0, 0, 0, 0);
                return saleDate >= start;
            });
        }
        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(sale => {
                if (!sale.date) return false;
                const saleDate = new Date(sale.date);
                return saleDate <= end;
            });
        }
        setFilteredSales(filtered);
        setCurrentPage(1);
    }, [sales, searchTerm, filterBy, billNumberFilter, customerNameFilter, phoneFilter, addedByFilter, paymentStatusFilter, startDate, endDate]);

    useEffect(() => { applyFilters(); }, [applyFilters]);

    const resetFilters = useCallback(() => {
        setFilterBy('');
        setBillNumberFilter('');
        setCustomerNameFilter('');
        setPhoneFilter('');
        setAddedByFilter('');
        setPaymentStatusFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
    }, []);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        } catch { return dateString; }
    }, []);

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return 'Rs. 0';
        return `Rs. ${Math.round(parseFloat(amount) || 0)}`;
    };

    const getPaymentStatus = (paid, grandTotal) => {
        const paidAmount = parseFloat(paid) || 0;
        const totalAmount = parseFloat(grandTotal) || 0;
        const balance = totalAmount - paidAmount;

        if (balance <= 0) {
            return { status: 'paid', text: translations.paid || 'Paid', color: '#10b981' };
        } else if (paidAmount === 0) {
            return { status: 'unpaid', text: translations.unpaid || 'Unpaid', color: '#ef4444' };
        } else {
            return { status: 'partial', text: translations.partiallyPaid || 'Partial', color: '#f59e0b' };
        }
    };

    const getPaymentMethodLabel = (method) => {
        const map = {
            'cash': translations.cash || 'Cash',
            'card': translations.card || 'Card',
            'upi': translations.upi || 'UPI',
            'bank_transfer': translations.bankTransfer || 'Bank Transfer'
        };
        return map[method] || method || '-';
    };

    const getTodayDate = useCallback(() => new Date().toISOString().split('T')[0], []);

    // Get rows per page - Landscape: 20 rows, Portrait: 15 rows
    const getRowsPerPage = useCallback(() => {
        if (pdfOrientation === 'landscape') {
            return 20;
        }
        return 15;
    }, [pdfOrientation]);

    const pagination = useMemo(() => {
        const totalPages = Math.ceil(filteredSales.length / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const currentSales = filteredSales.slice(startIndex, endIndex);
        return { totalPages, startIndex, endIndex, currentSales };
    }, [filteredSales, currentPage, itemsPerPage]);

    // PDF EXPORT FUNCTION
    const exportToPDF = useCallback(async () => {
        if (!filteredSales || filteredSales.length === 0) {
            showToast('warning', translations.noDataToExport || 'No data to export');
            return;
        }
        setPdfLoading(true);
        try {
            const currentLang = currentLanguage;
            const isRTL = RTL_LANGUAGE_CODES.has(currentLang);

            const pdfTextColor = '#1f2937';
            const pdfSubTextColor = '#6b7280';
            const pdfBorderColor = '#e5e7eb';
            const pdfHeaderBgColor = '#f9fafb';
            const pdfRowEvenBg = '#ffffff';
            const pdfRowOddBg = '#f9fafb';
            const pdfThemeColor = currentTheme;

            const pageSizeMap = { 'a4': 'a4', 'letter': 'letter', 'legal': 'legal', 'a5': 'a5' };
            const pageFormat = pageSizeMap[pdfPageSize] || 'a4';
            const pdf = new jsPDF({ orientation: pdfOrientation, unit: 'mm', format: pageFormat, compress: true });
            const pageWidth = pdf.internal.pageSize.getWidth();
            const rowsPerPage = getRowsPerPage();

            let salesToExport = [...filteredSales];

            if (pdfPageRange === 'range') {
                const totalPagesForRange = Math.ceil(salesToExport.length / rowsPerPage);
                const start = Math.max(1, pdfStartPage);
                const end = Math.min(totalPagesForRange, pdfEndPage);
                const startIdx = (start - 1) * rowsPerPage;
                const endIdx = Math.min(end * rowsPerPage, salesToExport.length);
                salesToExport = salesToExport.slice(startIdx, endIdx);
            }

            const totalPages = Math.ceil(salesToExport.length / rowsPerPage);

            const getPDFTranslation = (key, fallback = '') => {
                return translations[key] || fallback;
            };

            const formatCurrencyForPDF = (amount) => {
                if (!amount && amount !== 0) return 'Rs. 0';
                return `Rs. ${Math.round(parseFloat(amount) || 0)}`;
            };

            const formatDateForPDF = (dateString) => {
                if (!dateString) return '-';
                try {
                    const date = new Date(dateString);
                    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                } catch { return dateString; }
            };

            const renderPage = async (pageSales, pageNum, startRowIndex) => {
                return new Promise((resolve, reject) => {
                    try {
                        const container = document.createElement('div');
                        container.style.width = '750px';
                        container.style.padding = '12px 15px';
                        container.style.backgroundColor = '#ffffff';
                        container.style.color = pdfTextColor;
                        container.style.position = 'absolute';
                        container.style.left = '-9999px';
                        container.style.top = '0';
                        container.style.fontFamily = isRTL
                            ? "'Jameel Noori Nastaleeq', 'Noto Nastaliq Urdu', 'Urdu Typesetting', serif"
                            : "'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
                        container.style.direction = isRTL ? 'rtl' : 'ltr';

                        const salesReportLabel = getPDFTranslation('salesReport', 'Sales Report');
                        const salesDirectoryLabel = getPDFTranslation('salesDirectory', 'Sales Directory');
                        const totalSalesLabel = getPDFTranslation('totalSales', 'Total Sales');
                        const generatedLabel = getPDFTranslation('generated', 'Generated');
                        const reportIdLabel = getPDFTranslation('reportId', 'Report ID');
                        const generatedByLabel = getPDFTranslation('generatedBy', 'Generated by');
                        const shopCodeLabel = getPDFTranslation('shopCode', 'Shop Code');
                        const filtersAppliedLabel = getPDFTranslation('filtersApplied', 'FILTERS APPLIED');
                        const searchLabel = getPDFTranslation('search', 'Search');
                        const fromLabel = getPDFTranslation('from', 'From');
                        const toLabel = getPDFTranslation('to', 'To');
                        const pageLabel = getPDFTranslation('page', 'Page');
                        const ofLabel = getPDFTranslation('of', 'of');
                        const billNumberLabel = getPDFTranslation('billNumber', 'Bill No');
                        const bnLabel = getPDFTranslation('bn', 'BN');
                        const customerNameLabel = getPDFTranslation('customerName', 'Customer');
                        const phoneLabel = getPDFTranslation('phone', 'Phone');
                        const dateLabel = getPDFTranslation('date', 'Date');
                        const grandTotalLabel = getPDFTranslation('grandTotal', 'Total');
                        const paidAmountLabel = getPDFTranslation('paidAmount', 'Paid');
                        const balanceLabel = getPDFTranslation('balance', 'Balance');
                        const paymentStatusLabel = getPDFTranslation('paymentStatus', 'Status');
                        const addedByLabel = getPDFTranslation('addedBy', 'Added By');

                        const totalGrandTotal = pageSales.reduce((sum, sale) => sum + (parseFloat(sale.grand_total) || 0), 0);
                        const totalPaid = pageSales.reduce((sum, sale) => sum + (parseFloat(sale.paid_amount) || 0), 0);
                        const totalBalance = pageSales.reduce((sum, sale) => sum + (parseFloat(sale.balance) || 0), 0);

                        const headerHTML = `
                            <div style="margin-bottom: 12px; text-align: center;">
                                <div style="font-size: 8px; letter-spacing: 3px; color: ${pdfSubTextColor}; margin-bottom: 4px; text-transform: uppercase; font-weight: 500;">${salesReportLabel}</div>
                                <h1 style="color: ${pdfTextColor}; font-size: 20px; margin: 0 0 4px 0; font-weight: 600;">
                                    ${escapeHtml(currentShop?.shop_name) || escapeHtml(salesDirectoryLabel)}
                                </h1>
                                <div style="width: 40px; height: 2px; background: ${pdfThemeColor}; margin: 6px auto;"></div>
                                ${businessInfo.businessname ? `<div style="color: ${pdfSubTextColor}; font-size: 10px; margin-top: 4px;">${escapeHtml(businessInfo.businessname)}</div>` : ''}
                                <div style="color: ${pdfSubTextColor}; font-size: 7px; margin-top: 4px;">
                                    ${totalSalesLabel}: ${salesToExport.length} | ${generatedLabel}: ${new Date().toLocaleString(currentLang)}
                                </div>
                            </div>
                            <div style="display: flex; justify-content: space-between; margin: 8px 0 10px 0; padding: 5px 0; border-top: 1px solid ${pdfBorderColor}; border-bottom: 1px solid ${pdfBorderColor}; ${isRTL ? 'flex-direction: row-reverse;' : ''}">
                                <div style="font-size: 6.5px; color: ${pdfSubTextColor};"><strong>${reportIdLabel}:</strong> #${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${String(pageNum).padStart(3, '0')}</div>
                                <div style="font-size: 6.5px; color: ${pdfSubTextColor};"><strong>${generatedByLabel}:</strong> ${escapeHtml(user?.fullname || user?.username || 'System')}</div>
                                <div style="font-size: 6.5px; color: ${pdfSubTextColor};"><strong>${shopCodeLabel}:</strong> ${escapeHtml(currentShop?.shop_code || 'N/A')}</div>
                            </div>
                        `;

                        const hasFilters = searchTerm || paymentStatusFilter || startDate || endDate;
                        const filterHTML = hasFilters ? `
                            <div style="margin: 6px 0; padding: 4px 10px; background: #f7f7f7; border-${isRTL ? 'right' : 'left'}: 3px solid ${pdfThemeColor}; font-size: 6.5px; color: ${pdfSubTextColor};">
                                <span style="font-weight: 600;">${filtersAppliedLabel}:</span> 
                                ${searchTerm ? `${searchLabel}: "${escapeHtml(searchTerm)}"` : ''}
                                ${paymentStatusFilter ? ` | ${getPDFTranslation('paymentStatus', 'Status')}: ${paymentStatusFilter === 'paid' ? getPDFTranslation('paid', 'Paid') : paymentStatusFilter === 'unpaid' ? getPDFTranslation('unpaid', 'Unpaid') : getPDFTranslation('partiallyPaid', 'Partial')}` : ''}
                                ${startDate ? ` | ${fromLabel}: ${startDate}` : ''}
                                ${endDate ? ` | ${toLabel}: ${endDate}` : ''}
                            </div>
                        ` : '';

                        let tableHTML = `
                            <table style="width: 100%; border-collapse: collapse; font-size: 6.5px; margin-top: 4px;">
                                <thead>
                                    <tr style="background-color: ${pdfHeaderBgColor}; border-bottom: 2px solid ${pdfThemeColor};">
                                        <th style="padding: 4px 3px; text-align: center; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 4%;">#</th>
                                        <th style="padding: 4px 3px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 10%;">${dateLabel}</th>
                                        <th style="padding: 4px 3px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 9%;">${billNumberLabel}</th>
                                        <th style="padding: 4px 3px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 8%;">${bnLabel}</th>
                                        <th style="padding: 4px 3px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 14%;">${customerNameLabel}</th>
                                        <th style="padding: 4px 3px; text-align: center; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 10%;">${phoneLabel}</th>
                                        <th style="padding: 4px 3px; text-align: right; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 10%;">${grandTotalLabel}</th>
                                        <th style="padding: 4px 3px; text-align: right; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 10%;">${paidAmountLabel}</th>
                                        <th style="padding: 4px 3px; text-align: right; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 10%;">${balanceLabel}</th>
                                        <th style="padding: 4px 3px; text-align: center; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 8%;">${paymentStatusLabel}</th>
                                        <th style="padding: 4px 3px; text-align: center; font-weight: 700; color: ${pdfTextColor}; width: 6%;">${addedByLabel}</th>
                                    </tr>
                                </thead>
                                <tbody>
                        `;

                        pageSales.forEach((sale, idx) => {
                            const rowNum = startRowIndex + idx + 1;
                            const isEven = idx % 2 === 0;
                            const paymentStatus = getPaymentStatus(sale.paid_amount, sale.grand_total);

                            tableHTML += `
                                <tr style="border-bottom: 0.5px solid ${pdfBorderColor}; background: ${isEven ? pdfRowEvenBg : pdfRowOddBg};">
                                    <td style="padding: 3px; text-align: center; color: ${pdfSubTextColor}; border-right: 0.5px solid ${pdfBorderColor};">${rowNum}</td>
                                    <td style="padding: 3px; text-align: ${isRTL ? 'right' : 'left'}; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor};">${formatDateForPDF(sale.date)}</td>
                                    <td style="padding: 3px; text-align: ${isRTL ? 'right' : 'left'}; color: ${pdfTextColor}; font-weight: 500; border-right: 0.5px solid ${pdfBorderColor}; font-family: monospace;">${escapeHtml(sale.bill_number) || '-'}</td>
                                    <td style="padding: 3px; text-align: ${isRTL ? 'right' : 'left'}; color: ${pdfTextColor}; font-weight: 500; border-right: 0.5px solid ${pdfBorderColor}; font-family: monospace;">${escapeHtml(sale.bn) || '-'}</td>
                                    <td style="padding: 3px; text-align: ${isRTL ? 'right' : 'left'}; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor};">${escapeHtml(sale.customer_name) || (translations.walkInCustomer || 'Walk-in')}</td>
                                    <td style="padding: 3px; text-align: center; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor}; font-family: monospace;">${escapeHtml(sale.customer_phone) || '—'}</td>
                                    <td style="padding: 3px; text-align: right; color: #059669; font-weight: 600; border-right: 0.5px solid ${pdfBorderColor};">${formatCurrencyForPDF(sale.grand_total)}</td>
                                    <td style="padding: 3px; text-align: right; color: #3b82f6; font-weight: 600; border-right: 0.5px solid ${pdfBorderColor};">${formatCurrencyForPDF(sale.paid_amount)}</td>
                                    <td style="padding: 3px; text-align: right; color: ${parseFloat(sale.balance) > 0 ? '#d97706' : '#059669'}; font-weight: 600; border-right: 0.5px solid ${pdfBorderColor};">${formatCurrencyForPDF(sale.balance)}</td>
                                    <td style="padding: 3px; text-align: center; color: ${paymentStatus.color}; font-weight: 600; border-right: 0.5px solid ${pdfBorderColor};">${paymentStatus.text}</td>
                                    <td style="padding: 3px; text-align: center; color: ${pdfTextColor};">${escapeHtml(sale.username) || '—'}</td>
                                </tr>
                            `;
                        });

                        const emptyRowsNeeded = rowsPerPage - pageSales.length;
                        for (let i = 0; i < emptyRowsNeeded; i++) {
                            tableHTML += `
                                <tr style="border-bottom: 0.5px solid ${pdfBorderColor}; background: ${(pageSales.length + i) % 2 === 0 ? pdfRowEvenBg : pdfRowOddBg};">
                                    <td style="padding: 3px; text-align: center; color: ${pdfSubTextColor}; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px;">&nbsp;</td>
                                </tr>
                            `;
                        }

                        tableHTML += `
                                    <tr style="border-top: 1px solid ${pdfBorderColor}; background-color: #f8f8f8;">
                                        <td colspan="6" style="padding: 4px 3px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700;">${getPDFTranslation('pageSummary', 'Page Summary')}</td>
                                        <td style="padding: 4px 3px; text-align: right; font-weight: 700; color: #059669;">${formatCurrencyForPDF(totalGrandTotal)}</td>
                                        <td style="padding: 4px 3px; text-align: right; font-weight: 700; color: #3b82f6;">${formatCurrencyForPDF(totalPaid)}</td>
                                        <td style="padding: 4px 3px; text-align: right; font-weight: 700; color: #d97706;">${formatCurrencyForPDF(totalBalance)}</td>
                                        <td colspan="2">&nbsp;</td>
                                    </tr>
                                </tbody>
                            </table>
                        `;

                        const footerHTML = `
                            <div style="margin-top: 8px; padding-top: 5px; text-align: center;">
                                <div style="width: 30px; height: 1px; background: ${pdfBorderColor}; margin: 0 auto 5px auto;"></div>
                                <div style="font-size: 6.5px; color: ${pdfSubTextColor};">${pageLabel} ${pageNum} ${ofLabel} ${totalPages}</div>
                                <div style="font-size: 5px; color: #aaa; margin-top: 3px;">${translations.poweredBy || 'Powered by OurZest'}</div>
                            </div>
                        `;

                        container.innerHTML = headerHTML + filterHTML + tableHTML + footerHTML;
                        document.body.appendChild(container);
                        const fontsReady = document.fonts?.ready || Promise.resolve();
                        fontsReady
                            .then(() => html2canvas(container, { scale: 2, backgroundColor: '#ffffff', logging: false }))
                            .then(canvas => {
                                document.body.removeChild(container);
                                resolve(canvas);
                            })
                            .catch(err => { document.body.removeChild(container); reject(err); });
                    } catch (err) { reject(err); }
                });
            };

            for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
                const startRow = (pageNum - 1) * rowsPerPage;
                const endRow = Math.min(pageNum * rowsPerPage, salesToExport.length);
                const pageSales = salesToExport.slice(startRow, endRow);
                const canvas = await renderPage(pageSales, pageNum, startRow);
                if (pageNum > 1) {
                    pdf.addPage();
                }
                const imgWidth = pageWidth - 20;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 5, imgWidth, imgHeight);
            }

            const fileName = `Sales_Report_${currentShop?.shop_name?.replace(/[^a-z0-9]/gi, '_') || 'Export'}_${new Date().toISOString().slice(0, 10)}.pdf`;
            pdf.save(fileName);
            showToast('success', `${translations.reportGenerated || 'Report generated!'} ${salesToExport.length} ${translations.sales || 'sales'} on ${totalPages} ${translations.pages || 'page(s)'}`);
        } catch (error) {
            console.error('PDF Error:', error);
            showToast('error', translations.pdfGenerationError || 'Error generating PDF');
        } finally {
            setPdfLoading(false);
        }
    }, [filteredSales, currentLanguage, pdfOrientation, pdfPageSize, pdfPageRange, pdfStartPage, pdfEndPage, translations, businessInfo, currentShop, user, showToast, getRowsPerPage, searchTerm, paymentStatusFilter, startDate, endDate, currentTheme]);



    const loadBusinessInfo = useCallback(async () => {
        if (!user || !user.username) return;
        try {
            const data = await getBusinessInfoOnce(user.username);
            if (isMountedRef.current) {
                // Check if we got valid data
                if (data && data.id) {
                    setBusinessInfo({
                        businessname: data.businessname || '',
                        bussinesslogo: data.bussinesslogo || '',
                        bussinesslogo_url: data.bussinesslogo || '',
                        businessaddress: data.businessaddress || '',
                        phonenumber: data.phonenumber || '',
                        email: data.email || '',
                        fullname: data.fullname || '',
                        shop_name: data.shop_name || '',
                        shop_code: data.shop_code || '',
                        shop_id: data.shop_id || '',
                        default_shop_id: data.default_shop_id || '',
                        role: data.role || '',
                        all_shops: data.all_shops || [],
                        cnotes: data.cnotes || '',
                        cdesign: Number(data.cdesign) === 1 ? 1 : 0,
                        cdesign_picture: data.cdesign_picture || '',
                        cdesign_picture_url: data.cdesign_picture_url || data.cdesign_picture || '',
                        cdesign_source: data.cdesign_source || 'current_user',
                        cdesign_source_user_id: data.cdesign_source_user_id || data.id || null
                    });
                } else {
                    // Fallback to user data
                    setBusinessInfo({
                        businessname: user.business_name || user.fullname || '',
                        bussinesslogo: '',
                        bussinesslogo_url: '',
                        businessaddress: user.business_address || '',
                        phonenumber: user.phonenumber || '',
                        email: user.email || '',
                        fullname: user.fullname || '',
                        shop_name: currentShop?.shop_name || '',
                        shop_code: currentShop?.shop_code || '',
                        shop_id: currentShop?.shop_id || '',
                        default_shop_id: currentShop?.shop_id || '',
                        role: user.role || '',
                        all_shops: [],
                        cnotes: user.cnotes || '',
                        cdesign: Number(user.cdesign) === 1 ? 1 : 0,
                        cdesign_picture: user.cdesign_picture || '',
                        cdesign_picture_url: user.cdesign_picture_url || ''
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
            if (isMountedRef.current) {
                // Fallback to user data on error
                setBusinessInfo({
                    businessname: user.business_name || user.fullname || '',
                    bussinesslogo: '',
                    bussinesslogo_url: '',
                    businessaddress: user.business_address || '',
                    phonenumber: user.phonenumber || '',
                    email: user.email || '',
                    fullname: user.fullname || '',
                    shop_name: currentShop?.shop_name || '',
                    shop_code: currentShop?.shop_code || '',
                    shop_id: currentShop?.shop_id || '',
                    default_shop_id: currentShop?.shop_id || '',
                    role: user.role || '',
                    all_shops: [],
                    cnotes: user.cnotes || '',
                    cdesign: Number(user.cdesign) === 1 ? 1 : 0,
                    cdesign_picture: user.cdesign_picture || '',
                    cdesign_picture_url: user.cdesign_picture_url || ''
                });
            }
        }
    }, [user, currentShop]);

    // ============================================================
    // SALES RECEIPT PRINT
    // FIXED BLUE INVOICE DESIGN
    // OPTIONAL ROOT_USERS CUSTOMER DESIGN BACKGROUND
    // ============================================================
const handlePrint = async (sale) => {
    if (!sale) return;

    showToast('info', 'Preparing sales receipt...');

    // ========================================================
    // LOAD COMPLETE SALE
    // ========================================================
    let saleData = sale;

    if (!sale.items && sale.id && currentShop) {
        try {
            const response = await fetch(SELL_API, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'get_record',
                    id: sale.id,
                    shop_id: currentShop.shop_id,
                    language: currentLanguage
                })
            });

            const result = await response.json();

            if (result.status !== 'success' || !result.data) {
                throw new Error('Sale details not found');
            }

            saleData = result.data;

        } catch (error) {
            console.error('Error fetching sale details for print:', error);
            showToast('error', 'Could not load sale details for printing');
            return;
        }
    }

    if (!saleData.items || saleData.items.length === 0) {
        showToast('warning', 'No items to print');
        return;
    }

    // ========================================================
    // PRINT WINDOW
    // ========================================================
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
        showToast('error', 'Please allow popups for printing');
        return;
    }

    // ========================================================
    // ESCAPE FUNCTIONS
    // ========================================================
    const escapeAttr = (value) => {
        if (value === null || value === undefined) return '';
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    };

    const escapeHtml = (value) => {
        if (value === null || value === undefined) return '';
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    };

    // ========================================================
    // BACKGROUND IMAGE
    // ========================================================
    const designFlagValue = businessInfo?.cdesign ?? user?.cdesign;
    const designEnabled = [1, '1', true, 'true', 'yes', 'on'].includes(designFlagValue);
    const designPictureValue = String(
        businessInfo?.cdesign_picture_url ||
        businessInfo?.cdesign_picture ||
        user?.cdesign_picture_url ||
        user?.cdesign_picture ||
        ''
    ).trim();

    let customDesignUrl = '';
    if (designEnabled && designPictureValue) {
        try {
            const candidateUrl = /^https?:\/\//i.test(designPictureValue)
                ? new URL(designPictureValue)
                : new URL(
                    `https://ourzestapp.online/oz_backend/uploads/cdesigns/${encodeURIComponent(designPictureValue.split('/').pop())}`
                );

            if (candidateUrl.protocol === 'https:' || candidateUrl.protocol === 'http:') {
                customDesignUrl = candidateUrl.href;
            }
        } catch (error) {
            console.warn('Invalid customer design picture URL; printing without a background.', error);
        }
    }

    const receiptBackgroundUrl = customDesignUrl;
    const hasCustomDesign = Boolean(customDesignUrl);
    const rawPrintNote = String(businessInfo?.cnotes || '').trim();
    const printNote = rawPrintNote.length > 700
        ? `${rawPrintNote.slice(0, 697).trimEnd()}...`
        : rawPrintNote;

    // ========================================================
    // COLORS
    // ========================================================
    const MINT = '#0d9488';
    const MINT_DARK = '#0f766e';
    const MINT_FAINT = '#f0fdfa';
    const BLACK = '#000000';
    const DARK_GRAY = '#1a1a1a';
    const GRAY = '#4a4a4a';
    const LIGHT_GRAY = '#d1d5db';
    const WHITE = '#ffffff';

    // ========================================================
    // BUSINESS INFORMATION
    // ========================================================
    const businessName = businessInfo?.businessname ||
        user?.business_name ||
        user?.fullname ||
        currentShop?.shop_name ||
        'OurZest Store';

    const businessLogo = businessInfo?.bussinesslogo_url ||
        businessInfo?.bussinesslogo ||
        '';

    const businessAddress = businessInfo?.businessaddress ||
        user?.business_address ||
        '';

    const businessPhone = businessInfo?.phonenumber ||
        user?.phonenumber ||
        '';

    const businessEmail = businessInfo?.email ||
        user?.email ||
        '';

    const shopName = currentShop?.shop_name ||
        businessInfo?.shop_name ||
        '';

    const shopCode = currentShop?.shop_code ||
        businessInfo?.shop_code ||
        '';

    // ========================================================
    // CUSTOMER
    // ========================================================
    const customerName = saleData.customer_name ||
        saleData.customer ||
        'Walk-in Customer';

    const customerPhone = saleData.customer_phone ||
        saleData.phone ||
        '';

    const customerAddress = saleData.customer_address ||
        saleData.address ||
        '';

    // ========================================================
    // INVOICE INFORMATION
    // ========================================================
    const bn = saleData.bn ||
        saleData.bill_number ||
        saleData.id ||
        '';

    const saleId = saleData.id || '';

    // ========================================================
    // MONEY FORMAT
    // ========================================================
    const money = (value) => {
        const amount = Number.parseFloat(value) || 0;
        return `Rs. ${Math.round(amount).toLocaleString('en-PK')}`;
    };

    // ========================================================
    // TOTALS
    // ========================================================
    const itemsSubtotal = saleData.items.reduce((sum, item) => {
        const total = Number.parseFloat(item.total);
        if (Number.isFinite(total)) return sum + total;
        return sum + ((Number(item.price) || 0) * (Number(item.quantity) || 0));
    }, 0);

    const subtotal = saleData.subtotal ?? itemsSubtotal;
    const tax = saleData.tax || 0;
    const discount = saleData.discount || 0;
    const grandTotal = saleData.grand_total ?? (Number(subtotal) + Number(tax) - Number(discount));
    const paidAmount = saleData.paid_amount ?? saleData.paid ?? grandTotal;
    const balance = saleData.balance ?? (Number(grandTotal) - Number(paidAmount));

    // ========================================================
    // PAYMENT METHOD
    // ========================================================
    const paymentMethodRaw = String(saleData.payment_method || 'cash').toLowerCase();
    const paymentMethods = {
        cash: translations.cash || 'Cash',
        card: translations.card || 'Card',
        upi: translations.upi || 'UPI',
        bank_transfer: translations.bankTransfer || 'Bank Transfer',
        bank: translations.bankTransfer || 'Bank Transfer',
        wallet: 'Digital Wallet',
        digital_wallet: 'Digital Wallet'
    };
    const paymentMethod = paymentMethods[paymentMethodRaw] || saleData.payment_method || '-';

    // ========================================================
    // PAYMENT STATUS
    // ========================================================
    let paymentStatus = translations.unpaid || 'Unpaid';
    if (Number(balance) <= 0) {
        paymentStatus = translations.paid || 'Paid';
    } else if (Number(paidAmount) > 0) {
        paymentStatus = translations.partiallyPaid || 'Partially Paid';
    }

    // ========================================================
    // RECEIPT DATE
    // ========================================================
    const receiptDate = (() => {
        const date = saleData.date ? new Date(saleData.date) : new Date();
        const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;
        return safeDate.toLocaleString(currentLanguage === 'ur' ? 'ur-PK' : 'en-PK', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    })();

    // ========================================================
    // Get current language for RTL support
    // ========================================================
    const currentLang = currentLanguage || 'en';
    const isRTL = RTL_LANGUAGE_CODES.has(currentLang);

    const printText = {
        invoice: translations.purchaseReceipt || 'Sales Invoice',
        billFrom: translations.billFrom || 'Bill From',
        billTo: translations.billTo || 'Bill To',
        invoiceDetails: translations.invoiceDetails || 'Invoice Details',
        invoiceNumber: translations.invoiceNumber || 'Invoice No',
        saleId: translations.saleId || 'Sale ID',
        date: translations.date || 'Date',
        payment: translations.payment || 'Payment',
        number: translations.serialNo || 'No',
        product: translations.productName || 'Product',
        price: translations.price || 'Price',
        quantity: translations.quantity || 'Qty',
        total: translations.total || 'Total',
        importantNote: translations.importantNote || 'Important Note',
        termsTitle: translations.termsTitle || 'Terms & Conditions',
        termsText: translations.termsText || 'Please verify products, quantities and amounts before leaving. This invoice should be retained for your purchase record and warranty claims, where applicable.',
        subtotal: translations.subtotal || 'Subtotal',
        tax: translations.tax || 'Tax',
        discount: translations.discount || 'Discount',
        paid: translations.paid || 'Paid',
        balance: translations.balance || 'Balance',
        shop: translations.shop || 'Shop',
        warranty: translations.warranty || 'Warranty'
    };
    
    // Urdu font family
    const urduFont = '"Jameel Noori Nastaleeq", "Noto Nastaliq Urdu", "Urdu Typesetting", serif';
    const pashtoFont = '"Noto Naskh Arabic", "Noto Sans Arabic", Tahoma, sans-serif';
    const defaultFont = '"Inter", "Segoe UI", "Aptos", Arial, Helvetica, sans-serif';
    
    let bodyFont = defaultFont;
    let headingFont = defaultFont;
    
    if (currentLang === 'ur') {
        bodyFont = urduFont;
        headingFont = urduFont;
    } else if (currentLang === 'ps' || currentLang === 'ar') {
        bodyFont = pashtoFont;
        headingFont = pashtoFont;
    }

    // ========================================================
    // PRODUCTS HTML
    // ========================================================
    const productsHTML = saleData.items.map((item, index) => {
        const productName = item.product_name || item.name || 'Product';
        const quantity = Number(item.quantity) || 0;
        const price = Number(item.price) || 0;
        const itemTotal = item.total ?? (price * quantity);
        const hasWarranty = Number(item?.WarrantyStatus ?? item?.warranty_status ?? 0) === 1 && Boolean(item?.warranty);

        return `
            <tr>
                <td class="number-col">${index + 1}</td>
                <td class="product-col">
                    <div class="product-name">${escapeHtml(productName)}</div>
                    ${hasWarranty ? `<div class="warranty">✓ ${escapeHtml(item.warranty || printText.warranty)}</div>` : ''}
                </td>
                <td class="center-col">${money(price)}</td>
                <td class="center-col">${quantity}</td>
                <td class="center-col total-col">${money(itemTotal)}</td>
            </tr>
        `;
    }).join('');

    // ========================================================
    // ICONS
    // ========================================================
    const iconMap = {
        phone: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${MINT}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>`,
        mail: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${MINT}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>`,
        store: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${MINT}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>`,
        mapPin: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${MINT}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`
    };

    // ========================================================
    // PRINT HTML
    // ========================================================
    printWindow.document.write(`<!DOCTYPE html>
<html dir="${isRTL ? 'rtl' : 'ltr'}" lang="${currentLang}">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${escapeHtml(printText.invoice)} ${escapeHtml(String(bn))}</title>
    <link href="https://fonts.googleapis.com/css2?family=Noto+Naskh+Arabic:wght@400;500;600;700&family=Noto+Nastaliq+Urdu:wght@400;500;600;700&display=swap" rel="stylesheet">
    <style>
        @page {
            size: A4 portrait;
            margin: 0;
        }

        * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
        }

        html, body {
            width: 210mm;
            min-width: 210mm;
            height: 297mm;
            min-height: 297mm;
            max-height: 297mm;
            margin: 0 !important;
            padding: 0 !important;
            background: ${WHITE};
            overflow: hidden;
        }

        body {
            position: relative;
            overflow: hidden;
            color: ${BLACK};
            background: ${WHITE};
            font-family: ${bodyFont};
            font-size: 10.5pt;
            line-height: 1.6;
            text-rendering: geometricPrecision;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
        }

        /* RTL specific styles */
        html[dir="rtl"] body {
            font-family: ${bodyFont};
            text-align: right;
        }

        html[dir="rtl"] .header-left {
            flex-direction: row-reverse;
        }

        html[dir="rtl"] .header-right {
            text-align: left;
        }

        html[dir="rtl"] .info-table td {
            text-align: right;
        }

        html[dir="rtl"] .info-table .row-item {
            text-align: right;
            justify-content: flex-start;
        }

        html[dir="rtl"] .summary-row {
            flex-direction: row;
        }

        html[dir="rtl"] .signature {
            text-align: left;
        }

        html[dir="rtl"] .signature-line {
            margin-left: 0;
            margin-right: auto;
        }

        html[dir="rtl"] .items-table th:nth-child(2),
        html[dir="rtl"] .items-table td:nth-child(2) {
            text-align: right;
        }

        .number-col,
        .center-col,
        .summary-value,
        .invoice-sub {
            direction: ltr;
            unicode-bidi: isolate;
        }

        /* Urdu specific font sizes */
        html[lang="ur"] body {
            font-size: 11.5pt;
            line-height: 1.9;
            font-family: ${urduFont};
        }

        html[lang="ur"] .business-name {
            font-size: 20pt;
            letter-spacing: 0.3px;
        }

        html[lang="ur"] .header-right .invoice-label {
            font-size: 16pt;
        }

        html[lang="ur"] .items-table {
            font-size: 8.5pt;
        }

        html[lang="ur"] .items-table thead th {
            font-size: 8pt;
            padding: 6px 4px;
        }

        html[lang="ur"] .items-table tbody td {
            font-size: 8.5pt;
            padding: 5px 4px;
            line-height: 1.8;
        }

        html[lang="ur"] .product-name {
            font-size: 8.5pt;
        }

        html[lang="ur"] .info-table {
            font-size: 8pt;
        }

        html[lang="ur"] .info-table td {
            padding: 6px 10px;
        }

        html[lang="ur"] .info-table .row-item {
            font-size: 8pt;
            padding: 3px 0;
        }

        html[lang="ur"] .summary-row {
            font-size: 7.5pt;
            padding: 3px 0;
        }

        html[lang="ur"] .terms-text,
        html[lang="ur"] .note-text {
            font-size: 7.5pt;
            line-height: 1.8;
        }

        html[lang="ur"] .terms-title,
        html[lang="ur"] .note-label {
            font-size: 7pt;
        }

        html[lang="ur"] .thank-you-text {
            font-size: 14pt;
        }

        html[lang="ur"] .footer-contact {
            font-size: 8.5pt;
        }

        html[lang="ur"] .powered {
            font-size: 7.5pt;
        }

        /* Pashto specific font sizes */
        html[lang="ps"] body {
            font-size: 11pt;
            line-height: 1.7;
            font-family: ${pashtoFont};
        }

        html[lang="ps"] .business-name {
            font-size: 19pt;
        }

        html[lang="ps"] .items-table {
            font-size: 8pt;
        }

        html[lang="ps"] .items-table tbody td {
            font-size: 8pt;
        }

        html[lang="ps"] .info-table {
            font-size: 7.5pt;
        }

        .invoice-page {
            position: relative;
            width: 210mm;
            height: 297mm;
            min-height: 297mm;
            max-height: 297mm;
            margin: 0;
            padding: 0;
            overflow: hidden;
            background: ${WHITE};
        }

        .watermark-logo {
            position: absolute;
            z-index: 0;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-15deg);
            width: 180mm;
            height: 180mm;
            opacity: 0.04;
            pointer-events: none;
            object-fit: contain;
        }

        .page-background {
            position: absolute;
            z-index: 1;
            top: 0;
            left: 0;
            width: 210mm;
            height: 297mm;
            min-width: 210mm;
            min-height: 297mm;
            display: block;
            object-fit: fill;
            object-position: center center;
            margin: 0;
            padding: 0;
            border: 0;
            pointer-events: none;
        }

        .invoice-page.has-custom-background .page-background {
            object-fit: cover !important;
        }

        .invoice-content {
            position: relative;
            z-index: 2;
            width: 100%;
            height: 100%;
            padding: 10mm 10mm 6mm;
            display: flex;
            flex-direction: column;
        }

        .top-section {
            flex: 1 0 auto;
        }

        .business-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 15px;
            margin-bottom: 6px;
            padding-bottom: 6px;
            border-bottom: 2px solid ${MINT};
        }

        .header-left {
            display: flex;
            align-items: center;
            gap: 15px;
            min-width: 0;
        }

        .logo-wrapper {
            position: relative;
            flex: 0 0 auto;
            width: 60px;
            height: 60px;
        }

        .logo-wrapper .business-logo {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: contain;
            opacity: 0.9;
        }

        .business-info {
            flex: 1;
            min-width: 0;
        }

        .business-name {
            font-size: 20pt;
            font-weight: 900;
            color: ${BLACK};
            text-transform: uppercase;
            letter-spacing: 0.5px;
            line-height: 1.1;
            overflow-wrap: anywhere;
        }

        .business-contact-row {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-top: 2px;
            font-size: 8pt;
            color: ${GRAY};
            flex-wrap: wrap;
        }

        .business-contact-row .contact-item {
            display: flex;
            align-items: center;
            gap: 4px;
        }

        .business-contact-row .separator {
            color: ${LIGHT_GRAY};
        }

        .header-right {
            text-align: right;
            flex-shrink: 0;
        }

        .header-right .invoice-label {
            font-size: 16pt;
            font-weight: 900;
            color: ${MINT};
            text-transform: uppercase;
            letter-spacing: 1.5px;
        }

        .header-right .invoice-sub {
            font-size: 6.5pt;
            color: ${GRAY};
            font-weight: 600;
            margin-top: 2px;
        }

        .info-table {
            width: 100%;
            border-collapse: collapse;
            margin: 6px 0 8px;
            font-size: 7.5pt;
            border: 1px solid ${LIGHT_GRAY};
            border-radius: 8px;
            overflow: hidden;
        }

        .info-table td {
            padding: 6px 10px;
            border-right: 1px solid ${LIGHT_GRAY};
            vertical-align: top;
            background: ${WHITE};
        }

        .info-table td:last-child {
            border-right: none;
        }

        html[dir="rtl"] .info-table td {
            border-right: none;
            border-left: 1px solid ${LIGHT_GRAY};
        }

        html[dir="rtl"] .info-table td:last-child {
            border-left: none;
        }

        .info-table .col-header {
            font-size: 6.5pt;
            font-weight: 800;
            color: ${MINT};
            text-transform: uppercase;
            letter-spacing: 0.5px;
            padding-bottom: 3px;
            border-bottom: 1px solid rgba(13, 148, 136, 0.15);
            display: block;
            margin-bottom: 3px;
        }

        .info-table .row-item {
            display: flex;
            align-items: center;
            gap: 5px;
            padding: 2px 0;
            font-size: 7.5pt;
            color: ${DARK_GRAY};
            border-bottom: 1px solid rgba(0, 0, 0, 0.03);
            text-align: left;
        }

        .info-table .row-item:last-child {
            border-bottom: none;
        }

        .info-table .row-item .label {
            font-weight: 600;
            color: ${BLACK};
            min-width: 65px;
        }

        .info-table .row-item .value {
            font-weight: 500;
            color: ${DARK_GRAY};
            overflow-wrap: anywhere;
        }

        .info-table .row-item .value-bold {
            font-weight: 700;
            color: ${BLACK};
            overflow-wrap: anywhere;
        }

        .info-table .row-item .icon-wrap {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 16px;
            flex-shrink: 0;
        }

        .items-table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
            table-layout: fixed;
            font-size: 7.5pt;
            border-radius: 10px;
            overflow: hidden;
            border: 1px solid ${LIGHT_GRAY};
        }

        .items-table thead {
            display: table-header-group;
        }

        .items-table thead th {
            padding: 6px 4px;
            color: ${WHITE};
            background: ${MINT};
            border: none;
            font-size: 7pt;
            font-weight: 900;
            text-align: center;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
        }

        .items-table tbody td {
            padding: 4px 4px;
            color: ${BLACK};
            border: none;
            border-bottom: 1px solid ${LIGHT_GRAY};
            font-size: 7.5pt;
            vertical-align: middle;
            background: ${WHITE};
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
        }

        .items-table tbody tr {
            break-inside: avoid;
            page-break-inside: avoid;
        }

        .items-table tbody tr:nth-child(even) td {
            background: ${MINT_FAINT};
        }

        .items-table tbody tr:last-child td {
            border-bottom: none;
        }

        .items-table tbody tr:last-child td:first-child {
            border-radius: 0 0 0 10px;
        }

        .items-table tbody tr:last-child td:last-child {
            border-radius: 0 0 10px 0;
        }

        .items-table th:nth-child(1), .items-table td:nth-child(1) {
            width: 6%;
            text-align: center;
        }
        .items-table th:nth-child(2), .items-table td:nth-child(2) {
            width: 42%;
            text-align: left;
        }
        .items-table th:nth-child(3), .items-table td:nth-child(3) {
            width: 17%;
            text-align: center;
        }
        .items-table th:nth-child(4), .items-table td:nth-child(4) {
            width: 12%;
            text-align: center;
        }
        .items-table th:nth-child(5), .items-table td:nth-child(5) {
            width: 23%;
            text-align: center;
        }

        html[dir="rtl"] .items-table th:nth-child(2),
        html[dir="rtl"] .items-table td:nth-child(2) {
            text-align: right;
        }

        .product-name {
            font-weight: 700;
            color: ${BLACK};
            font-size: 7.5pt;
            overflow-wrap: anywhere;
        }

        .warranty {
            display: inline-block;
            margin-top: 1px;
            color: ${MINT_DARK};
            font-size: 5.5pt;
            font-weight: 600;
        }

        .center-col {
            text-align: center;
        }

        .total-col {
            font-weight: 700;
            color: ${MINT_DARK};
        }

        .lower-section {
            display: grid;
            grid-template-columns: 1fr 0.7fr;
            gap: 15px;
            margin-top: 6px;
        }

        .terms-left {
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
        }

        .terms-title {
            font-size: 6.5pt;
            font-weight: 900;
            color: ${MINT};
            text-transform: uppercase;
            letter-spacing: 0.3px;
            margin-bottom: 3px;
        }

        .terms-text {
            color: ${DARK_GRAY};
            font-size: 6.5pt;
            line-height: 1.6;
        }

        .note-text {
            color: ${DARK_GRAY};
            font-size: 6.5pt;
            line-height: 1.6;
            margin-bottom: 5px;
        }

        .note-text .note-label {
            font-size: 6.5pt;
            font-weight: 900;
            color: ${MINT};
            text-transform: uppercase;
            letter-spacing: 0.3px;
        }

        .summary {
            width: 100%;
        }

        .summary-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            padding: 2px 0;
            color: ${BLACK};
            font-size: 7pt;
        }

        .summary-label {
            font-weight: 700;
            text-transform: uppercase;
            color: ${BLACK};
        }

        .summary-value {
            font-weight: 700;
            white-space: nowrap;
            color: ${BLACK};
        }

        .summary-total {
            margin-top: 3px;
            padding: 4px 6px;
            border-top: 2px solid ${MINT};
            border-bottom: 2px solid ${MINT};
            background: ${MINT_FAINT};
            border-radius: 6px;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
        }

        .summary-total .summary-label {
            font-size: 8pt;
            font-weight: 900;
            color: ${MINT};
        }

        .summary-total .summary-value {
            font-size: 8pt;
            font-weight: 900;
            color: ${MINT_DARK};
        }

        .balance-row {
            margin-top: 2px;
        }

        .balance-row .summary-value {
            font-weight: 900;
            color: ${MINT};
        }

        .signature {
            margin-top: 12px;
            text-align: right;
        }

        .signature-line {
            width: 70px;
            margin-left: auto;
            border-top: 1px solid ${BLACK};
        }

        html[dir="rtl"] .signature {
            text-align: left;
        }

        html[dir="rtl"] .signature-line {
            margin-left: 0;
            margin-right: auto;
        }

        .signature-text {
            margin-top: 2px;
            color: ${GRAY};
            font-size: 6pt;
            font-weight: 700;
        }

        .thank-you-section {
            text-align: center;
            padding-top: 4px;
        }

        .thank-you-text {
            font-family: ${currentLang === 'ur' ? urduFont : currentLang === 'ps' ? pashtoFont : '"Georgia", "Times New Roman", serif'};
            font-size: 14pt;
            font-weight: 700;
            color: ${MINT_DARK};
            font-style: ${currentLang === 'ur' || currentLang === 'ps' ? 'normal' : 'italic'};
            letter-spacing: 0.3px;
        }

        .footer-contact {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-wrap: wrap;
            gap: 14px;
            padding-top: 3px;
            color: ${DARK_GRAY};
            font-size: 8pt;
            font-weight: 600;
        }

        .footer-contact .footer-icon {
            display: inline-flex;
            align-items: center;
            gap: 4px;
        }

        .footer-dot {
            color: ${LIGHT_GRAY};
            font-size: 8pt;
        }

        .powered {
            margin-top: 5px;
            color: ${BLACK};
            text-align: center;
            font-size: 7pt;
            font-weight: 700;
            letter-spacing: 0.3px;
        }

        .powered strong {
            color: ${MINT};
            font-size: 8pt;
            font-weight: 900;
            letter-spacing: 0.5px;
        }

        @media print {
            html, body {
                width: 210mm !important;
                height: 297mm !important;
                min-height: 297mm !important;
                max-height: 297mm !important;
                margin: 0 !important;
                padding: 0 !important;
                background: ${WHITE} !important;
                overflow: hidden !important;
            }

            .invoice-page {
                width: 210mm !important;
                height: 297mm !important;
                min-height: 297mm !important;
                max-height: 297mm !important;
                margin: 0 !important;
                padding: 0 !important;
                overflow: hidden !important;
            }

            .invoice-content {
                display: flex !important;
                flex-direction: column !important;
                height: 100% !important;
                padding: 10mm 10mm 6mm !important;
            }

            .top-section {
                flex: 1 0 auto !important;
            }

            .page-background {
                position: absolute !important;
                top: 0 !important;
                left: 0 !important;
                width: 210mm !important;
                height: 297mm !important;
                object-fit: fill !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
            }

            .items-table thead th,
            .items-table tbody td {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
            }

            .items-table thead {
                display: table-header-group;
            }

            .items-table tbody tr {
                break-inside: avoid !important;
                page-break-inside: avoid !important;
            }
        }

        .business-header,
        .info-table,
        .lower-section,
        .thank-you-section,
        .footer-contact,
        .powered {
            break-inside: avoid;
            page-break-inside: avoid;
        }

        .items-table tbody tr {
            break-inside: avoid;
            page-break-inside: avoid;
        }
    </style>
</head>
<body>

<main class="invoice-page${hasCustomDesign ? ' has-custom-background' : ''}">

    <!-- WATERMARK LOGO -->
    ${businessLogo ? `
        <img src="${escapeAttr(businessLogo)}" class="watermark-logo" alt=""
            onerror="this.style.display='none';"
        />
    ` : ''}

    <!-- CUSTOMER BACKGROUND -->
    ${hasCustomDesign ? `
        <img src="${escapeAttr(receiptBackgroundUrl)}" class="page-background" alt=""
            onerror="this.style.display='none'; var page=this.closest('.invoice-page'); if(page){page.classList.remove('has-custom-background');}"
        />
    ` : ''}

    <div class="invoice-content">

        <div class="top-section">

            <!-- HEADER -->
            <header class="business-header">
                <div class="header-left">
                    ${businessLogo ? `
                        <div class="logo-wrapper">
                            <img src="${escapeAttr(businessLogo)}" class="business-logo"
                                alt="${escapeAttr(businessName)}"
                                onerror="this.parentElement.style.display='none';"
                            />
                        </div>
                    ` : ''}
                    <div class="business-info">
                        <div class="business-name">${escapeHtml(businessName)}</div>
                        <div class="business-contact-row">
                            ${businessAddress ? `<span class="contact-item"><span class="icon-wrap">${iconMap.mapPin}</span> ${escapeHtml(businessAddress)}</span>` : ''}
                            ${businessAddress && businessPhone ? `<span class="separator">|</span>` : ''}
                            ${businessPhone ? `<span class="contact-item"><span class="icon-wrap">${iconMap.phone}</span> ${escapeHtml(businessPhone)}</span>` : ''}
                            ${businessPhone && businessEmail ? `<span class="separator">|</span>` : ''}
                            ${businessEmail ? `<span class="contact-item"><span class="icon-wrap">${iconMap.mail}</span> ${escapeHtml(businessEmail)}</span>` : ''}
                        </div>
                    </div>
                </div>
                <div class="header-right">
                    <div class="invoice-label">${escapeHtml(printText.invoice)}</div>
                    <div class="invoice-sub">#${escapeHtml(String(bn))}</div>
                </div>
            </header>

            <!-- INFO TABLE -->
            <table class="info-table">
                <tbody>
                    <tr>
                        <td style="width: 33%;">
                            <span class="col-header">${escapeHtml(printText.billFrom)}</span>
                            <div class="row-item"><span class="value-bold">${escapeHtml(businessName)}</span></div>
                            ${businessAddress ? `<div class="row-item"><span class="icon-wrap">${iconMap.mapPin}</span> <span class="value">${escapeHtml(businessAddress)}</span></div>` : ''}
                            ${businessPhone ? `<div class="row-item"><span class="icon-wrap">${iconMap.phone}</span> <span class="value">${escapeHtml(businessPhone)}</span></div>` : ''}
                            ${businessEmail ? `<div class="row-item"><span class="icon-wrap">${iconMap.mail}</span> <span class="value">${escapeHtml(businessEmail)}</span></div>` : ''}
                            ${shopName ? `<div class="row-item"><span class="icon-wrap">${iconMap.store}</span> <span class="value">${escapeHtml(printText.shop)}: ${escapeHtml(shopName)}${shopCode ? ` (${escapeHtml(shopCode)})` : ''}</span></div>` : ''}
                        </td>
                        <td style="width: 33%;">
                            <span class="col-header">${escapeHtml(printText.billTo)}</span>
                            <div class="row-item"><span class="value-bold">${escapeHtml(customerName)}</span></div>
                            ${customerPhone ? `<div class="row-item"><span class="icon-wrap">${iconMap.phone}</span> <span class="value">${escapeHtml(customerPhone)}</span></div>` : ''}
                            ${customerAddress ? `<div class="row-item"><span class="icon-wrap">${iconMap.mapPin}</span> <span class="value">${escapeHtml(customerAddress)}</span></div>` : ''}
                        </td>
                        <td style="width: 34%;">
                            <span class="col-header">${escapeHtml(printText.invoiceDetails)}</span>
                            <div class="row-item"><span class="label">${escapeHtml(printText.invoiceNumber)}:</span> <span class="value-bold">${escapeHtml(String(bn))}</span></div>
                            ${saleId ? `<div class="row-item"><span class="label">${escapeHtml(printText.saleId)}:</span> <span class="value-bold">#${escapeHtml(String(saleId))}</span></div>` : ''}
                            <div class="row-item"><span class="label">${escapeHtml(printText.date)}:</span> <span class="value">${escapeHtml(receiptDate)}</span></div>
                            <div class="row-item"><span class="label">${escapeHtml(printText.payment)}:</span> <span class="value">${escapeHtml(paymentMethod)} • ${escapeHtml(paymentStatus)}</span></div>
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- PRODUCTS -->
            <table class="items-table">
                <thead>
                    <tr>
                        <th>${escapeHtml(printText.number)}</th>
                        <th>${escapeHtml(printText.product)}</th>
                        <th>${escapeHtml(printText.price)}</th>
                        <th>${escapeHtml(printText.quantity)}</th>
                        <th>${escapeHtml(printText.total)}</th>
                    </tr>
                </thead>
                <tbody>
                    ${productsHTML}
                </tbody>
            </table>

            <!-- PAYMENT / SUMMARY -->
            <section class="lower-section">
                <div class="terms-left">
                    ${printNote ? `
                        <div class="note-text">
                            <span class="note-label">${escapeHtml(printText.importantNote)}:</span><br />
                            ${escapeHtml(printNote)}
                        </div>
                    ` : ''}
                    <div class="terms-title">${escapeHtml(printText.termsTitle)}</div>
                    <div class="terms-text">${escapeHtml(printText.termsText)}</div>
                </div>
                <div>
                    <div class="summary">
                        <div class="summary-row">
                            <span class="summary-label">${escapeHtml(printText.subtotal)}</span>
                            <span class="summary-value">${money(subtotal)}</span>
                        </div>
                        ${Number(tax) > 0 ? `
                            <div class="summary-row">
                                <span class="summary-label">${escapeHtml(printText.tax)}</span>
                                <span class="summary-value">${money(tax)}</span>
                            </div>
                        ` : ''}
                        ${Number(discount) > 0 ? `
                            <div class="summary-row">
                                <span class="summary-label">${escapeHtml(printText.discount)}</span>
                                <span class="summary-value">-${money(discount)}</span>
                            </div>
                        ` : ''}
                        <div class="summary-row summary-total">
                            <span class="summary-label">${escapeHtml(printText.total)}</span>
                            <span class="summary-value">${money(grandTotal)}</span>
                        </div>
                        <div class="summary-row">
                            <span class="summary-label">${escapeHtml(printText.paid)}</span>
                            <span class="summary-value">${money(paidAmount)}</span>
                        </div>
                        <div class="summary-row balance-row">
                            <span class="summary-label">${escapeHtml(printText.balance)}</span>
                            <span class="summary-value">${money(balance)}</span>
                        </div>
                    </div>
                   
                </div>
            </section>

        </div>

      

    </div>

</main>

<script>
    window.onload = async function() {
        try {
            const images = Array.from(document.images);
            await Promise.all(images.map(function(img) {
                if (img.complete && img.naturalWidth > 0) {
                    return Promise.resolve();
                }
                return new Promise(function(resolve) {
                    img.onload = resolve;
                    img.onerror = resolve;
                });
            }));

            if (document.fonts && document.fonts.ready) {
                await document.fonts.ready;
            }
        } catch (error) {
            console.error('Receipt loading error:', error);
        }

        setTimeout(function() {
            window.focus();
            window.print();
        }, 80);
    };
</script>

</body>
</html>`);

    printWindow.document.close();
    printWindow.focus();
};
    // Previous purchase-style printer retained only as an unused reference.
    const handlePrintBill = async (purchase) => {
        if (!purchase) return;

        showToast('info', 'Loading purchase details for print...');

        let purchaseData = purchase;

        if (!purchase.items && purchase.id && currentShop) {
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_purchases.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'get_record',
                        id: purchase.id,
                        shop_id: currentShop.shop_id,
                        language: currentLanguage
                    })
                });
                const data = await response.json();
                if (data.status === 'success' && data.data) {
                    purchaseData = data.data;
                } else {
                    showToast('error', 'Could not load purchase details for printing');
                    return;
                }
            } catch (error) {
                console.error('Error fetching purchase details for print:', error);
                showToast('error', 'Could not load purchase details for printing');
                return;
            }
        }

        if (!purchaseData.items || purchaseData.items.length === 0) {
            showToast('warning', 'No items to print receipt');
            return;
        }

        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            showToast('error', 'Please allow popups for printing');
            return;
        }

        const shopName = businessInfo?.businessname || currentShop?.shop_name || 'OurZest Store';
        const shopAddress = businessInfo?.businessaddress || '';
        const businessPhone = businessInfo?.phonenumber || '';
        const businessLogo = businessInfo?.bussinesslogo_url || businessInfo?.bussinesslogo || '';
        const purchaseBusinessName = splitBusinessName(shopName);
        const supplierName = purchaseData.supplier_name || 'Walk-in Supplier';

        const formatReceiptDateTime = (dateString) => {
            const date = dateString ? new Date(dateString) : new Date();
            const validDate = isNaN(date.getTime()) ? new Date() : date;
            const datePart = validDate.toLocaleDateString('en-IN', {
                day: '2-digit', month: '2-digit', year: 'numeric'
            });
            const timePart = validDate.toLocaleTimeString('en-IN', {
                hour: '2-digit', minute: '2-digit', hour12: true
            });
            return `${datePart} ${timePart}`;
        };

        const formatReceiptCurrency = (amount) => {
            if (!amount && amount !== 0) return 'Rs. 0';
            return `Rs. ${Math.round(parseFloat(amount) || 0)}`;
        };

        const receiptDateTime = formatReceiptDateTime(purchaseData.date);
        const grandTotal = purchaseData.grand_total || '0';
        const paidAmount = purchaseData.paid || '0';
        const balance = purchaseData.balance || Math.round(parseFloat(grandTotal) - parseFloat(paidAmount));
        const tax = purchaseData.tax || 0;
        const discount = purchaseData.discount || 0;

        printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
        <title>Purchase Bill ${purchaseData.bill_no}</title>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
            @page {
                size: A4 portrait;
                margin: 10mm 15mm;
            }
            * { 
                margin: 0; 
                padding: 0; 
                box-sizing: border-box; 
            }
            body {
                background: #ffffff;
                font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
                color: #1f2937;
                font-size: 10pt;
                line-height: 1.5;
                padding: 0;
                margin: 0;
                display: flex;
                justify-content: center;
                align-items: flex-start;
                min-height: 100vh;
            }
            .receipt-wrapper {
                width: 100%;
                max-width: 1000px;
                padding: 0;
                margin: 0 auto;
            }
            .receipt { 
                width: 100%; 
                padding: 5px 0;
                margin: 0 auto;
            }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .text-left { text-align: left; }
            .fw-900 { font-weight: 900; }
            .fw-700 { font-weight: 700; }
            .border-bottom { 
                border-bottom: 2px solid #e5e7eb; 
                padding-bottom: 8px; 
                margin-bottom: 8px; 
            }
            .border-top { 
                border-top: 2px solid #e5e7eb; 
                padding-top: 8px; 
                margin-top: 8px; 
            }
            .border-dash { 
                border-bottom: 1px dashed #d1d5db; 
                padding-bottom: 5px; 
                margin-bottom: 5px; 
            }
            .flex-row { 
                display: flex; 
                justify-content: space-between; 
                align-items: baseline; 
                flex-wrap: wrap;
                gap: 4px 15px;
            }
            .flex-col { display: flex; flex-direction: column; }
            .gap-1 { gap: 4px; }
            .mt-1 { margin-top: 6px; }
            .mb-1 { margin-bottom: 6px; }
            .p-1 { padding: 4px; }
            .bg-light { background: #f9fafb; }

            /* HEADER STYLES */
            .header-center {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 18px;
                min-height: 140px;
                margin-left: -18px;
                text-align: left;
                border-bottom: 3px double #e5e7eb;
                padding: 14px 4px 22px;
                margin-bottom: 12px;
            }
            .header-logo {
                display: block;
                flex: 0 0 auto;
                margin: 0;
                max-height: 112px;
                max-width: 158px;
                width: 158px;
                height: auto;
                object-fit: contain;
            }
            .header-business-copy { min-width: 0; text-align: left; }
            .business-name-lockup { display: inline-flex; flex-direction: column; width: max-content; max-width: 100%; }
            .business-name-primary {
                font-size: 28pt;
                font-weight: 900;
                text-transform: uppercase;
                letter-spacing: .5px;
                line-height: 1.02;
                color: #1f2937;
            }
            .business-name-secondary {
                display: flex;
                justify-content: space-between;
                width: 100%;
                margin-top: 4px;
                font-size: 17pt;
                font-weight: 650;
                letter-spacing: .3px;
                line-height: 1.1;
                color: #374151;
            }
            .business-phone {
                font-size: 12pt;
                font-weight: 600;
                margin-top: 5px;
                color: #4b5563;
            }
            .business-addr {
                font-size: 11pt;
                font-weight: 500;
                margin-top: 2px;
                color: #6b7280;
            }

            .receipt-title {
                text-align: center;
                font-size: 16pt;
                font-weight: 800;
                letter-spacing: 3px;
                color: #1f2937;
                border-bottom: 2px solid #e5e7eb;
                padding-bottom: 6px;
                margin-bottom: 6px;
            }
            .receipt-bill-number {
                text-align: center;
                font-size: 12pt;
                font-weight: 700;
                margin-bottom: 4px;
                color: #374151;
            }
            .receipt-bill-number strong { font-weight: 900; color: #1f2937; }
            .receipt-customer {
                text-align: center;
                font-size: 12pt;
                font-weight: 700;
                margin-bottom: 6px;
                color: #374151;
            }
            .receipt-customer strong { font-weight: 900; color: #1f2937; }

            /* META INFO */
            .bill-meta {
                display: flex;
                justify-content: space-between;
                font-size: 10.5pt;
                font-weight: 600;
                padding: 4px 0;
                border-bottom: 1px solid #e5e7eb;
                margin-bottom: 6px;
                flex-wrap: wrap;
                gap: 4px 12px;
                color: #4b5563;
            }
            .bill-meta strong { color: #1f2937; }

            .customer-box {
                background: #f9fafb;
                padding: 8px 12px;
                border-left: 4px solid #6366f1;
                border: 1px solid #e5e7eb;
                border-left-width: 4px;
                margin: 6px 0 10px 0;
                font-size: 10.5pt;
                border-radius: 4px;
            }
            .customer-box .label { font-weight: 600; color: #4b5563; }

            .datetime-center {
                text-align: center;
                font-size: 11pt;
                font-weight: 600;
                padding: 4px 0;
                margin-bottom: 6px;
                color: #4b5563;
            }
            .datetime-center strong { color: #1f2937; }

            /* ITEMS TABLE */
            .items-table {
                width: 100%;
                border-collapse: collapse;
                margin: 8px 0 12px 0;
                border: 1px solid #d1d5db;
                border-radius: 4px;
                overflow: hidden;
                font-size: 9.5pt;
            }
            .items-table thead {
                background: #f3f4f6;
            }
            .items-table th {
                text-align: center;
                padding: 8px 6px;
                font-size: 9pt;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                font-weight: 700;
                color: #374151;
                border-bottom: 2px solid #d1d5db;
                border-right: 1px solid #e5e7eb;
            }
            .items-table th:last-child {
                border-right: none;
            }
            .items-table td {
                padding: 7px 6px;
                border: 1px solid #e5e7eb;
                font-size: 10pt;
                font-weight: 500;
                vertical-align: middle;
                text-align: center;
                color: #1f2937;
            }
            .items-table th:first-child, 
            .items-table td:first-child { 
                text-align: center; 
                width: 6%;
            }
            .items-table th:nth-child(2), 
            .items-table td:nth-child(2) { 
                text-align: left; 
                width: 44%;
            }
            .items-table th:nth-child(3), 
            .items-table td:nth-child(3) { 
                text-align: center; 
                width: 12%;
            }
            .items-table th:nth-child(4), 
            .items-table td:nth-child(4) { 
                text-align: right; 
                width: 16%;
            }
            .items-table th:nth-child(5), 
            .items-table td:nth-child(5) { 
                text-align: right; 
                width: 16%;
            }
            .items-table tr:nth-child(even) {
                background: #fafafa;
            }
            .items-table tr:hover {
                background: #f3f4f6;
            }
            .product-name { 
                font-weight: 600; 
                word-break: break-word; 
                color: #1f2937;
            }
            .product-meta {
                display: block;
                font-size: 8pt;
                color: #6b7280;
                font-weight: 400;
                margin-top: 2px;
            }

            /* PAYMENT BOX */
            .payment-box {
                margin-top: 8px;
                padding-top: 8px;
                border-top: 2px solid #e5e7eb;
                max-width: 400px;
                margin-left: auto;
            }
            .payment-row {
                display: flex;
                justify-content: space-between;
                padding: 3px 0;
                font-size: 11pt;
                font-weight: 600;
                color: #374151;
            }
            .payment-row .label { color: #4b5563; font-weight: 500; }
            .payment-row .value { font-weight: 600; }
            .payment-row.total {
                font-size: 13pt;
                font-weight: 900;
                border-top: 2px solid #1f2937;
                margin-top: 4px;
                padding-top: 6px;
                color: #1f2937;
            }
            .payment-row.total .value {
                color: #059669;
            }
            .payment-row .value.text-danger { color: #dc2626; }
            .payment-row .value.text-primary { color: #3b82f6; }
            .payment-row .value.text-warning { color: #d97706; }
            .payment-row .value.text-success { color: #059669; }

            /* FOOTER */
            .footer {
                text-align: center;
                font-size: 9.5pt;
                margin-top: 12px;
                padding-top: 10px;
                border-top: 2px solid #e5e7eb;
                font-weight: 500;
                color: #6b7280;
            }
            .thankyou { 
                font-size: 12pt; 
                font-weight: 700; 
                color: #1f2937;
                margin-bottom: 4px;
            }
            .powered-by {
                font-size: 8pt;
                color: #9ca3af;
                letter-spacing: 0.5px;
            }

            /* RESPONSIVE - A5 and smaller */
            @media print and (max-width: 160mm) {
                body { font-size: 9pt; }
                .business-name-primary { font-size: 22pt; }
                .business-name-secondary { font-size: 13.5pt; }
                .receipt-title { font-size: 13pt; }
                .items-table { font-size: 8.5pt; }
                .items-table th, .items-table td { padding: 5px 4px; }
                .payment-box { max-width: 300px; }
                .payment-row { font-size: 10pt; }
                .payment-row.total { font-size: 12pt; }
                .header-center { min-height: 106px; margin-left: -12px; gap: 14px; padding: 8px 2px 14px; }
                .header-logo { max-height: 80px; max-width: 112px; width: 112px; }
            }

            @media print and (max-width: 120mm) {
                .items-table th, .items-table td { padding: 4px 3px; font-size: 7.5pt; }
                .business-name-primary { font-size: 18pt; }
                .business-name-secondary { font-size: 11pt; }
                .receipt-title { font-size: 11pt; }
                .payment-box { max-width: 250px; }
                .payment-row { font-size: 9pt; }
                .payment-row.total { font-size: 10.5pt; }
            }

            @media print {
                body { margin: 0; padding: 0; }
                .no-print { display: none; }
            }

            /* PAPER SIZE OVERRIDES via data attributes */
            @media print and (size: A5) {
                body { font-size: 8.5pt; }
                .business-name-primary { font-size: 19pt; }
                .business-name-secondary { font-size: 12pt; }
                .items-table th, .items-table td { padding: 4px 4px; font-size: 8pt; }
            }
            @media print and (size: LEGAL) {
                body { font-size: 10.5pt; }
                .items-table { font-size: 10pt; }
            }
        </style>
    </head>
    <body>
        <div class="receipt-wrapper">
            <div class="receipt">
                <!-- HEADER -->
                <div class="header-center">
                    ${businessLogo ? `<img src="${businessLogo}" alt="logo" class="header-logo" onerror="this.style.display='none'" />` : ''}
                    <div class="header-business-copy">
                        <div class="business-name-lockup">
                            <div class="business-name-primary">${escapeHtml(purchaseBusinessName.primary)}</div>
                            ${purchaseBusinessName.secondary ? `<div class="business-name-secondary">${purchaseBusinessName.secondaryUnits.map(unit => `<span>${escapeHtml(unit)}</span>`).join('')}</div>` : ''}
                        </div>
                        ${businessPhone ? `<div class="business-phone">Phone: ${escapeHtml(businessPhone)}</div>` : ''}
                        ${shopAddress ? `<div class="business-addr">Address: ${escapeHtml(shopAddress)}</div>` : ''}
                    </div>
                </div>

                <!-- TITLE -->
                <div class="receipt-title">${translations.purchaseReceipt || 'PURCHASE RECEIPT'}</div>
                <div class="receipt-bill-number"><strong>${translations.billNumber || 'Bill No'}:</strong> #${escapeHtml(purchaseData.bill_no || '')}</div>
                <div class="receipt-customer">${translations.supplier || 'Supplier'}: <strong>${escapeHtml(supplierName)}</strong></div>

                <!-- DATE/TIME -->
                <div class="datetime-center">
                    <strong>${translations.date || 'Date'}:</strong> ${receiptDateTime}
                </div>

                <!-- SUPPLIER CONTACT INFO -->
                ${(purchaseData.phone || purchaseData.address) ? `
                <div class="customer-box">
                    ${purchaseData.phone ? `<div><span class="label">📞 Phone:</span> ${escapeHtml(purchaseData.phone)}</div>` : ''}
                    ${purchaseData.address ? `<div><span class="label">📍 Address:</span> ${escapeHtml(purchaseData.address)}</div>` : ''}
                </div>` : ''}

                <!-- ITEMS TABLE -->
                <table class="items-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>${translations.productName || 'Product'}</th>
                            <th>${translations.quantity || 'Qty'}</th>
                            <th>${translations.price || 'Price'}</th>
                            <th>${translations.total || 'Total'}</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${purchaseData.items.map((item, idx) => `
                            <tr>
                                <td>${idx + 1}</td>
                                <td>
                                    <div class="product-name">${escapeHtml(item.product_name || 'Product')}</div>
                                    ${(item.batch_no || item.barcode) ? `
                                    <span class="product-meta">
                                        ${item.batch_no ? `Batch: ${escapeHtml(item.batch_no)}` : ''}
                                        ${item.batch_no && item.barcode ? ' | ' : ''}
                                        ${item.barcode ? `Barcode: ${escapeHtml(item.barcode)}` : ''}
                                    </span>` : ''}
                                </td>
                                <td>${Math.round(parseFloat(item.quantity) || 0)}</td>
                                <td>${formatReceiptCurrency(item.price)}</td>
                                <td>${formatReceiptCurrency(item.total || item.price * item.quantity)}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>

                <!-- PAYMENT SUMMARY -->
                <div class="payment-box">
                    <div class="payment-row">
                        <span class="label">${translations.subtotal || 'Subtotal'}</span>
                        <span class="value">${formatReceiptCurrency(grandTotal)}</span>
                    </div>
                    ${parseFloat(tax) > 0 ? `
                    <div class="payment-row">
                        <span class="label">${translations.tax || 'Tax'}</span>
                        <span class="value">${formatReceiptCurrency(tax)}</span>
                    </div>` : ''}
                    ${parseFloat(discount) > 0 ? `
                    <div class="payment-row">
                        <span class="label">${translations.discount || 'Discount'}</span>
                        <span class="value text-danger">-${formatReceiptCurrency(discount)}</span>
                    </div>` : ''}
                    <div class="payment-row total">
                        <span class="label">${translations.grandTotal || 'GRAND TOTAL'}</span>
                        <span class="value">${formatReceiptCurrency(grandTotal)}</span>
                    </div>
                    ${parseFloat(paidAmount) > 0 ? `
                    <div class="payment-row">
                        <span class="label">${translations.paidAmount || 'Paid'}</span>
                        <span class="value text-primary">${formatReceiptCurrency(paidAmount)}</span>
                    </div>` : ''}
                    ${parseFloat(balance) > 0 ? `
                    <div class="payment-row">
                        <span class="label">${translations.balance || 'Balance'}</span>
                        <span class="value text-warning">${formatReceiptCurrency(balance)}</span>
                    </div>` : ''}
                    ${parseFloat(balance) <= 0 && parseFloat(paidAmount) > 0 ? `
                    <div class="payment-row">
                        <span class="label">${translations.balance || 'Balance'}</span>
                        <span class="value text-success">${formatReceiptCurrency(balance)}</span>
                    </div>` : ''}
                </div>

                <!-- FOOTER -->
                <div class="footer">
                    <div class="thankyou">${translations.thankYou || 'Thank you for your business!'}</div>
                    <div class="powered-by">Developed by OurZest PVT LTD.</div>
                </div>
            </div>
        </div>

        <script>
            // Paper size selector for print
            const paperSize = localStorage.getItem('receipt_paper_size') || 'A4';
            const orientation = localStorage.getItem('receipt_orientation') || 'portrait';
            
            // Apply paper size via @page
            const style = document.createElement('style');
            style.textContent = \`
                @page {
                    size: \${paperSize} \${orientation};
                    margin: 10mm 15mm;
                }
            \`;
            document.head.appendChild(style);

            window.onload = function() {
                setTimeout(function() {
                    window.print();
                    window.onafterprint = function() { window.close(); };
                }, 500);
            };
        </script>
    </body>
    </html>
`);

        printWindow.document.close();
        printWindow.focus();
    };
    const handleView = async (sale) => {
        setSelectedSale(sale);

        if (!sale.items && sale.id && currentShop) {
            try {
                const response = await fetch(SELL_API, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'get_record',
                        id: sale.id,
                        shop_id: currentShop.shop_id,
                        language: currentLanguage
                    })
                });
                const data = await response.json();
                if (data.status === 'success' && data.data) {
                    setSelectedSale(data.data);
                }
            } catch (error) {
                console.error('Error fetching sale details:', error);
            }
        }

        setShowViewModal(true);
    };

    const handleEdit = async (sale) => {
        let saleData = sale;
        try {
            const response = await fetch(SELL_API, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_record', id: sale.id, username: user?.username, shop_id: currentShop?.shop_id, language: currentLanguage })
            });
            const result = await response.json();
            if (result.status !== 'success' || !result.data) throw new Error(result.message || 'Could not load sale');
            saleData = result.data;
        } catch (error) { showToast('error', error.message || 'Could not load sale'); return; }
        const editableItems = (saleData.items || []).map(item => ({
            ...item,
            product_name: sourceValue(item, 'product_name'),
            supplier_name: sourceValue(item, 'supplier_name'),
            company: sourceValue(item, 'company'),
            WarrantyStatus: Number(item.WarrantyStatus ?? item.warranty_status ?? 0) === 1 ? 1 : 0,
            issue_warranty: Number(item.WarrantyStatus ?? item.warranty_status ?? 0) === 1,
            product_warranty: item.product_warranty || item.warranty || '',
            quantity: String(item.display_quantity ?? item.quantity ?? 1), price: String(item.price ?? ''),
            total: String(item.total ?? '0.00'), available_stock: Number(item.available_stock ?? 0) + Number(item.quantity ?? 0),
            sell_method: item.sell_method || (item.sell_unit === 'pack' ? 'packing' : 'sub_packing')
        }));
        setSelectedSale(saleData);
        setEditFormData({
            id: saleData.id, date: saleData.date ? saleData.date.split('T')[0] : '',
            bill_number: saleData.bill_number || '', bn: saleData.bn || '', customer_name: sourceValue(saleData, 'customer_name'),
            customer_phone: saleData.customer_phone || '', customer_address: sourceValue(saleData, 'customer_address'),
            subtotal: saleData.subtotal || '', tax: saleData.tax || '', discount: saleData.discount || '',
            grand_total: saleData.grand_total || '', paid_amount: saleData.paid_amount || '', balance: saleData.balance || '',
            received_amount: saleData.received_amount ?? saleData.paid_amount ?? '',
            payment_locked: Math.abs(Number(saleData.received_amount ?? saleData.paid_amount ?? 0) - Number(saleData.paid_amount ?? 0)) > 0.01
                || Number(saleData.previous_balance ?? 0) > 0.01,
            payment_method: saleData.payment_method || 'cash', payment_status: saleData.payment_status || 'unpaid',
            notes: sourceValue(saleData, 'notes'), courier_name: sourceValue(saleData, 'courier_name'),
            original_language: saleData.original_language || currentLanguage,
            tracking_number: saleData.tracking_number || '', packing_status: saleData.packing_status || 'unpacked',
            items: editableItems
        });
        setEditFormErrors({});
        setShowEditModal(true);
    };

    const handleDeleteClick = (saleId) => {
        const sale = sales.find(s => s.id === saleId);
        if (!sale) return;
        if (sale.username !== user?.username) {
            showToast('error', 'You can only delete sales you added');
            return;
        }
        setSaleToDelete(saleId);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!saleToDelete || !currentShop) return;
        const sale = sales.find(s => s.id === saleToDelete);
        if (!sale || sale.username !== user?.username) {
            showToast('error', 'You can only delete your own sales');
            setShowDeleteModal(false);
            setSaleToDelete(null);
            return;
        }
        try {
            const response = await fetch(SELL_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'delete',
                    id: saleToDelete,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.deletedSuccess || 'Sale deleted successfully');
                fetchSales();
                setShowDeleteModal(false);
                setSaleToDelete(null);
            } else {
                showToast('error', data.message || translations.deleteError || 'Error deleting sale');
            }
        } catch (error) {
            console.error('Error deleting sale:', error);
            showToast('error', translations.deleteError || 'Error deleting sale');
        }
    };

    const handleUpdateSale = async (e) => {
        e.preventDefault();
        if (!selectedSale || !currentShop) return;

        const errors = {};
        if (!editFormData.date) errors.date = translations.dateRequired || 'Date is required';
        if (!editFormData.bill_number) errors.bill_number = translations.billRequired || 'Bill number is required';
        if (String(editFormData.bn || '').trim() && !/^[A-Za-z0-9]+$/.test(String(editFormData.bn).trim())) {
            errors.bn = 'BN may contain letters and numbers only';
        }
        editFormData.items?.forEach((item, index) => {
            if (!item.product_id) errors.items = `Select a valid product on row ${index + 1}`;
            if (!(Number(item.quantity) > 0)) errors.items = `Enter a valid quantity on row ${index + 1}`;
            if (!(Number(item.price) >= 0)) errors.items = `Enter a valid price on row ${index + 1}`;
        });
        if (parseFloat(editFormData.paid_amount) > parseFloat(editFormData.grand_total)) {
            errors.paid_amount = 'Paid amount cannot exceed grand total';
        }

        if (Object.keys(errors).length > 0) {
            setEditFormErrors(errors);
            return;
        }

        setEditLoading(true);
        try {
            const response = await fetch(SELL_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'edit',
                    id: selectedSale.id,
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    language: currentLanguage,
                    original_language: editFormData.original_language || selectedSale.original_language || currentLanguage,
                    date: editFormData.date,
                    bill_number: editFormData.bill_number,
                    bn: String(editFormData.bn || '').trim(),
                    customer_name: editFormData.customer_name,
                    customer_phone: editFormData.customer_phone,
                    customer_address: editFormData.customer_address,
                    subtotal: editFormData.subtotal,
                    tax: editFormData.tax,
                    discount: editFormData.discount,
                    grand_total: editFormData.grand_total,
                    paid_amount: editFormData.paid_amount,
                    received_amount: editFormData.received_amount,
                    balance: editFormData.balance,
                    payment_method: editFormData.payment_method,
                    payment_status: editFormData.payment_status,
                    notes: editFormData.notes,
                    courier_name: editFormData.courier_name,
                    tracking_number: editFormData.tracking_number,
                    packing_status: editFormData.packing_status,
                    items: editFormData.items.map(item => ({
                        ...item,
                        product_name: sourceValue(item, 'product_name'),
                        supplier_name: sourceValue(item, 'supplier_name'),
                        company: sourceValue(item, 'company'),
                        original_language: item.original_language || editFormData.original_language || currentLanguage
                    }))
                })
            });
            const responseText = await response.text();
            let data;
            try {
                data = responseText ? JSON.parse(responseText) : null;
            } catch (parseError) {
                throw new Error(
                    `Sale update returned invalid JSON (HTTP ${response.status}): ${responseText.slice(0, 180) || 'empty response'}`
                );
            }
            if (!response.ok || !data || data.status !== 'success') {
                const operation = data?.operation ? ` [${data.operation}]` : '';
                throw new Error((data?.message || `Sale update failed (${response.status})`) + operation);
            }
            if (data.status === 'success') {
                const hasItemConfirmation = data.data?.items_updated !== undefined && data.data?.items_updated !== null;
                const savedItemCount = Number(data.data?.items_updated);
                if (hasItemConfirmation && (!Number.isInteger(savedItemCount) || savedItemCount !== editFormData.items.length)) {
                    throw new Error(`Server did not confirm sale items (expected ${editFormData.items.length}, received ${data.data?.items_updated ?? 'none'})`);
                }
                showToast('success', translations.saleUpdated || 'Sale updated successfully');
                setShowEditModal(false);
                fetchSales();
            } else {
                showToast('error', data.message || translations.updateError || 'Error updating sale');
            }
        } catch (error) {
            console.error('Error updating sale:', error);
            showToast('error', error?.message || translations.updateError || 'Error updating sale');
        } finally {
            setEditLoading(false);
        }
    };

    const handlePageChange = useCallback((page) => { if (page >= 1 && page <= pagination.totalPages) setCurrentPage(page); }, [pagination.totalPages]);

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value));
        setCurrentPage(1);
    };

    const isRTL = RTL_LANGUAGE_CODES.has(currentLanguage);

    return (
        <div
            className={`ozlistui-container ${isRTL ? 'ozlistui-rtl' : 'ozlistui-ltr'} ${darkMode ? 'ozlistui-dark' : ''}`}
            dir={isRTL ? 'rtl' : 'ltr'}
            lang={currentLanguage}
        >
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;500;600;700&display=swap');
                .ozlistui-container.ozlistui-rtl,
                .ozlistui-container.ozlistui-rtl input,
                .ozlistui-container.ozlistui-rtl textarea,
                .ozlistui-container.ozlistui-rtl select,
                .ozlistui-container.ozlistui-rtl button {
                    font-family: 'Jameel Noori Nastaleeq', 'Noto Nastaliq Urdu', 'Urdu Typesetting', serif !important;
                }
                .ozlistui-container.ozlistui-rtl input[type='number'],
                .ozlistui-container.ozlistui-rtl input[type='tel'],
                .ozlistui-container.ozlistui-rtl .ozlistui-money,
                .ozlistui-container.ozlistui-rtl .ozlistui-number {
                    direction: ltr;
                    unicode-bidi: isolate;
                    text-align: right;
                }
            `}</style>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
                darkMode={darkMode}
            />

            <div className="ozlistui-sticky-header">
                <div className="ozlistui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozlistui-header-content">
                    <div className="ozlistui-header-left">
                        <div className="ozlistui-header-title">
                            <h1>{translations.salesList || 'Sales List'}</h1>
                            <p>{translations.formDetails || 'All sales details are below'}</p>
                        </div>
                    </div>
                    <div className="ozlistui-header-right">
                        <button
                            type="button"
                            className="ozlistui-header-filter-btn"
                            onClick={() => changeLanguage(currentLanguage === 'ur' ? 'en' : 'ur')}
                            title={currentLanguage === 'ur' ? 'Switch to English' : 'اردو میں دیکھیں'}
                            aria-label={currentLanguage === 'ur' ? 'Switch to English' : 'اردو میں دیکھیں'}
                        >
                            <span aria-hidden="true">文</span>
                            <span>{currentLanguage === 'ur' ? 'English' : 'اردو'}</span>
                        </button>
                        <button
                            className="ozlistui-header-filter-btn"
                            onClick={() => setShowFilters(!showFilters)}
                            title={showFilters ? (translations.hideFilters || 'Hide Filters') : (translations.filters || 'Filters')}
                        >
                            <FontAwesomeIcon icon={showFilters ? faEyeSlash : faFilter} />
                            <span>{showFilters ? (translations.hideFilters || 'Hide Filters') : (translations.filters || 'Filters')}</span>
                        </button>

                        {!currentShop && (
                            <button className="ozlistui-header-shop-btn" onClick={() => setShowShopSelector(true)}>
                                <FontAwesomeIcon icon={faExchangeAlt} />
                                <span>{translations.switchShop || 'Switch Shop'}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {showFilters && (
                <div className="ozlistui-filters-section">
                    <div className="ozlistui-filters-row">
                        <CustomDropdown
                            label={translations.filterBy || 'Filter By'}
                            icon={faTag}
                            value={filterBy}
                            onChange={setFilterBy}
                            options={filterByOptions}
                            translations={translations}
                        />

                        {filterBy === 'billNumber' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="text"
                                        id="billNumberFilter"
                                        value={billNumberFilter}
                                        onChange={(e) => setBillNumberFilter(e.target.value)}
                                        placeholder=" "
                                    />
                                    <label htmlFor="billNumberFilter">
                                        <FontAwesomeIcon icon={faTag} />
                                        {translations.billNumberFilter || 'Bill Number'}
                                    </label>
                                </div>
                            </div>
                        )}

                        {filterBy === 'customerName' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="text"
                                        id="customerNameFilter"
                                        value={customerNameFilter}
                                        onChange={(e) => setCustomerNameFilter(e.target.value)}
                                        placeholder=" "
                                    />
                                    <label htmlFor="customerNameFilter">
                                        <FontAwesomeIcon icon={faUser} />
                                        {translations.customerNameFilter || 'Customer Name'}
                                    </label>
                                </div>
                            </div>
                        )}

                        {filterBy === 'phone' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="tel"
                                        id="phoneFilter"
                                        value={phoneFilter}
                                        onChange={(e) => setPhoneFilter(e.target.value)}
                                        placeholder=" "
                                        dir="ltr"
                                    />
                                    <label htmlFor="phoneFilter">
                                        <FontAwesomeIcon icon={faPhone} />
                                        {translations.phoneFilter || 'Phone Number'}
                                    </label>
                                </div>
                            </div>
                        )}

                        {filterBy === 'added_by' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="text"
                                        id="addedByFilter"
                                        value={addedByFilter}
                                        onChange={(e) => setAddedByFilter(e.target.value)}
                                        placeholder=" "
                                    />
                                    <label htmlFor="addedByFilter">
                                        <FontAwesomeIcon icon={faUser} />
                                        {translations.addedByFilter || 'Added By'}
                                    </label>
                                </div>
                            </div>
                        )}

                        {filterBy === 'date' && (
                            <>
                                <div className="ozlistui-filter-group ozlistui-date-filter">
                                    <div className="ozlistui-floating-wrapper">
                                        <input
                                            type="date"
                                            id="startDate"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            max={getTodayDate()}
                                            placeholder=" "
                                        />
                                        <label htmlFor="startDate">
                                            <FontAwesomeIcon icon={faCalendar} />
                                            {translations.startDate || 'Start Date'}
                                        </label>
                                    </div>
                                </div>
                                <div className="ozlistui-filter-group ozlistui-date-filter">
                                    <div className="ozlistui-floating-wrapper">
                                        <input
                                            type="date"
                                            id="endDate"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            min={startDate}
                                            max={getTodayDate()}
                                            placeholder=" "
                                        />
                                        <label htmlFor="endDate">
                                            <FontAwesomeIcon icon={faCalendar} />
                                            {translations.endDate || 'End Date'}
                                        </label>
                                    </div>
                                </div>
                            </>
                        )}

                        {filterBy === 'paymentStatus' && (
                            <CustomDropdown
                                label={translations.paymentStatusFilter || 'Payment Status'}
                                icon={faCreditCard}
                                value={paymentStatusFilter}
                                onChange={setPaymentStatusFilter}
                                options={paymentStatusOptions}
                                translations={translations}
                            />
                        )}

                        <CustomDropdown
                            label={translations.orientation || 'Orientation'}
                            icon={faFilePdf}
                            value={pdfOrientation}
                            onChange={setPdfOrientation}
                            options={pdfOrientationOptions}
                            translations={translations}
                        />

                        <CustomDropdown
                            label={translations.pageSize || 'Page Size'}
                            icon={faFilePdf}
                            value={pdfPageSize}
                            onChange={setPdfPageSize}
                            options={pdfPageSizeOptions}
                            translations={translations}
                        />

                        <CustomDropdown
                            label="Page Range"
                            icon={faFilePdf}
                            value={pdfPageRange}
                            onChange={setPdfPageRange}
                            options={pdfPageRangeOptions}
                            translations={translations}
                        />

                        {pdfPageRange === 'range' && (
                            <>
                                <div className="ozlistui-filter-group" style={{ width: '70px' }}>
                                    <div className="ozlistui-floating-wrapper">
                                        <input
                                            type="number"
                                            min="1"
                                            value={pdfStartPage}
                                            onChange={(e) => setPdfStartPage(Math.max(1, parseInt(e.target.value) || 1))}
                                            placeholder=" "
                                        />
                                        <label>{translations.from || 'From'}</label>
                                    </div>
                                </div>
                                <div className="ozlistui-filter-group" style={{ width: '70px' }}>
                                    <div className="ozlistui-floating-wrapper">
                                        <input
                                            type="number"
                                            min={pdfStartPage}
                                            value={pdfEndPage}
                                            onChange={(e) => setPdfEndPage(Math.max(pdfStartPage, parseInt(e.target.value) || pdfStartPage))}
                                            placeholder=" "
                                        />
                                        <label>{translations.to || 'To'}</label>
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="ozlistui-filter-group">
                            <button onClick={fetchSales} className="ozlistui-refresh-btn" disabled={loading}>
                                <FontAwesomeIcon icon={faRefresh} />
                                <span className="ozlistui-btn-text">{translations.refresh || 'Refresh'}</span>
                            </button>
                        </div>

                        <div className="ozlistui-filter-group">
                            <button
                                onClick={exportToPDF}
                                className="ozlistui-refresh-btn"
                                disabled={filteredSales.length === 0 || pdfLoading}
                                style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: 'white' }}
                            >
                                {pdfLoading ? <div className="ozlistui-btn-spinner-small"></div> : <FontAwesomeIcon icon={faFilePdf} />}
                                <span className="ozlistui-btn-text">{translations.exportToPDF || 'Export PDF'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="ozlistui-loading-container">
                    <div className="ozlistui-loading-spinner"></div>
                    <p>{translations.loadingUserData || 'Loading your sales data...'}</p>
                </div>
            ) : !currentShop ? (
                <div className="ozlistui-no-shop-container">
                    <div className="ozlistui-no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>{translations.selectShop || 'Please select a shop to view sales'}</p>
                        <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => setShowShopSelector(true)}>
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="ozlistui-table-scroll">
                        <div className="ozlistui-table-container">
                            <div className="ozlistui-table-wrapper">
                                <table className="ozlistui-table">
                                    <thead>
                                        <tr>
                                            <th className="ozlistui-id-column ozlistui-centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="ozlistui-centered-cell">{translations.date || 'Date'}</th>

                                            <th className="ozlistui-centered-cell">{translations.bn || 'BN'}</th>
                                            <th className="ozlistui-centered-cell">{translations.customerName || 'Customer'}</th>
                                            <th className="ozlistui-centered-cell">{translations.grandTotal || 'Total'}</th>
                                            <th className="ozlistui-centered-cell">{translations.paidAmount || 'Paid'}</th>
                                            <th className="ozlistui-centered-cell">{translations.balance || 'Balance'}</th>
                                            <th className="ozlistui-centered-cell">{translations.paymentStatus || 'Status'}</th>
                                            <th className="ozlistui-centered-cell">{translations.paymentMethod || 'Payment'}</th>
                                            <th className="ozlistui-centered-cell">{translations.addedBy || 'Added By'}</th>
                                            <th className="ozlistui-actions-column ozlistui-centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pagination.currentSales.map((sale, index) => {
                                            const paymentStatus = getPaymentStatus(sale.paid_amount, sale.grand_total);
                                            const addedBy = sale.username || '-';
                                            const isSelfAdded = addedBy === user?.username;

                                            return (
                                                <tr key={sale.id}>
                                                    <td className="ozlistui-id-cell ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text">{pagination.startIndex + index + 1}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text">{formatDate(sale.date)}</span>
                                                    </td>

                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{sale.bn || '-'}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text">{sale.customer_name || (translations.walkInCustomer || 'Walk-in')}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text" style={{ color: '#059669', fontWeight: 'bold' }}>{formatCurrency(sale.grand_total)}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text" style={{ color: '#3b82f6', fontWeight: 'bold' }}>{formatCurrency(sale.paid_amount)}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text" style={{ color: parseFloat(sale.balance) > 0 ? '#d97706' : '#059669', fontWeight: 'bold' }}>{formatCurrency(sale.balance)}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <div className="ozlistui-payment-badge" style={{ backgroundColor: `${paymentStatus.color}20`, color: paymentStatus.color, border: `1px solid ${paymentStatus.color}40`, display: 'inline-block', padding: '0.2rem 0.6rem', fontSize: '0.7rem' }}>
                                                            {paymentStatus.text}
                                                        </div>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text">{getPaymentMethodLabel(sale.payment_method)}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className={`ozlistui-added-by-badge ${isSelfAdded ? 'ozlistui-self' : ''}`}>
                                                            {addedBy}
                                                        </span>
                                                    </td>
                                                    <td className="ozlistui-actions-cell ozlistui-centered-cell">
                                                        <div className="ozlistui-actions-content">
                                                            <button onClick={() => handlePrint(sale)} className="ozlistui-action-btn ozlistui-print-btn" title={translations.print || 'Print'}>
                                                                <FontAwesomeIcon icon={faPrint} />
                                                            </button>
                                                            <button onClick={() => handleView(sale)} className="ozlistui-action-btn ozlistui-view-btn" title={translations.view || 'View'}>
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </button>
                                                            <button onClick={() => handleEdit(sale)} className="ozlistui-action-btn ozlistui-edit-btn" title={translations.edit || 'Edit'} disabled={!isSelfAdded} style={{ opacity: isSelfAdded ? 1 : 0.5, cursor: isSelfAdded ? 'pointer' : 'not-allowed' }}>
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button onClick={() => handleDeleteClick(sale.id)} className="ozlistui-action-btn ozlistui-delete-btn" title={translations.delete || 'Delete'} disabled={!isSelfAdded} style={{ opacity: isSelfAdded ? 1 : 0.5, cursor: isSelfAdded ? 'pointer' : 'not-allowed' }}>
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {filteredSales.length > 0 && currentShop && (
                        <div className="ozlistui-pagination">
                            <div className="ozlistui-pagination-left">
                                <div className="ozlistui-per-page">
                                    <span>{translations.show || 'Show'}</span>
                                    <select
                                        value={itemsPerPage}
                                        onChange={(e) => {
                                            setItemsPerPage(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                        className="ozlistui-per-page-select"
                                    >
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                        <option value={500}>500</option>
                                        <option value={1000}>1000</option>
                                    </select>
                                    <span>{translations.perPage || 'per page'}</span>
                                </div>
                                <div className="ozlistui-pagination-info">
                                    <span>
                                        {translations.showing || 'Showing'}
                                        <strong> {pagination.startIndex + 1} </strong>
                                        {translations.to || 'to'}
                                        <strong> {Math.min(pagination.startIndex + itemsPerPage, filteredSales.length)} </strong>
                                        {translations.of || 'of'}
                                        <strong> {filteredSales.length} </strong>
                                        {translations.sales || 'sales'}
                                    </span>
                                </div>
                            </div>
                            <div className="ozlistui-pagination-right">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="ozlistui-pagination-btn"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{translations.previous || 'Previous'}</span>
                                </button>
                                <div className="ozlistui-page-numbers">
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (pagination.totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= pagination.totalPages - 2) {
                                            pageNum = pagination.totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }
                                        return (
                                            <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`ozlistui-page-number ${currentPage === pageNum ? 'ozlistui-active' : ''}`}>
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>
                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === pagination.totalPages}
                                    className="ozlistui-pagination-btn"
                                >
                                    <span>{translations.next || 'Next'}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal */}
            {showViewModal && selectedSale && (
                <div className={`ozlistui-modal-overlay ${darkMode ? 'ozlistui-dark' : ''}`} onClick={() => setShowViewModal(false)}>
                    <div className="ozlistui-modal-content ozlistui-view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="ozlistui-modal-header">
                            <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faReceipt} /></div>
                            <div className="ozlistui-modal-title">
                                <h3>{translations.saleDetails || 'Sale Details'}</h3>
                                <span className="ozlistui-sale-number-badge">#{selectedSale.bill_number}</span>
                            </div>
                            <button className="ozlistui-close-btn" onClick={() => setShowViewModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="ozlistui-modal-body ozlistui-view-modal-body">
                            <div className="ozlistui-view-section">
                                    <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faInfoCircle} />{translations.saleInformation || 'Sale Information'}</h4>
                                <div className="ozlistui-view-grid">
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.saleId || 'Sale ID'}:</span><span className="ozlistui-view-value">#{selectedSale.id}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.date || 'Date'}:</span><span className="ozlistui-view-value">{formatDate(selectedSale.date)}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.billNumber || 'Bill Number'}:</span><span className="ozlistui-view-value" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{selectedSale.bill_number}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.bn || 'BN'}:</span><span className="ozlistui-view-value" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{selectedSale.bn || '-'}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.customerName || 'Customer Name'}:</span><span className="ozlistui-view-value">{selectedSale.customer_name || (translations.walkInCustomer || 'Walk-in Customer')}</span></div>
                                    {selectedSale.customer_phone && (
                                        <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.phone || 'Phone'}:</span><span className="ozlistui-view-value">{selectedSale.customer_phone}</span></div>
                                    )}
                                    {selectedSale.customer_address && (
                                        <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.customerAddress || 'Address'}:</span><span className="ozlistui-view-value">{selectedSale.customer_address}</span></div>
                                    )}
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.paymentMethod || 'Payment Method'}:</span><span className="ozlistui-view-value">{getPaymentMethodLabel(selectedSale.payment_method)}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.addedBy || 'Added By'}:</span><span className="ozlistui-view-value"><span className="ozlistui-added-by-badge">{selectedSale.username || '-'}</span></span></div>
                                    {selectedSale.notes && (
                                        <div className="ozlistui-view-row ozlistui-full-width"><span className="ozlistui-view-label">{translations.notes || 'Notes'}:</span><span className="ozlistui-view-value">{selectedSale.notes}</span></div>
                                    )}
                                </div>
                            </div>

                            <div className="ozlistui-view-section">
                                    <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faCalculator} />{translations.paymentSummary || 'Payment Summary'}</h4>
                                <div className="ozlistui-view-grid">
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.subtotal || 'Subtotal'}:</span><span className="ozlistui-view-value">{formatCurrency(selectedSale.subtotal)}</span></div>
                                    {parseFloat(selectedSale.tax) > 0 && <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.tax || 'Tax'}:</span><span className="ozlistui-view-value">{formatCurrency(selectedSale.tax)}</span></div>}
                                    {parseFloat(selectedSale.discount) > 0 && <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.discount || 'Discount'}:</span><span className="ozlistui-view-value">-{formatCurrency(selectedSale.discount)}</span></div>}
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.grandTotal || 'Grand Total'}:</span><span className="ozlistui-view-value" style={{ color: '#059669', fontWeight: 'bold' }}>{formatCurrency(selectedSale.grand_total)}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.paidAmount || 'Paid Amount'}:</span><span className="ozlistui-view-value" style={{ color: '#3b82f6', fontWeight: 'bold' }}>{formatCurrency(selectedSale.paid_amount)}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.balance || 'Balance'}:</span><span className="ozlistui-view-value" style={{ color: parseFloat(selectedSale.balance) > 0 ? '#d97706' : '#059669', fontWeight: 'bold' }}>{formatCurrency(selectedSale.balance)}</span></div>
                                </div>
                            </div>

                            {selectedSale.items && selectedSale.items.length > 0 && (
                                <div className="ozlistui-view-section">
                                    <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faBoxes} />{translations.items || 'Items'}</h4>
                                    <div className="ozlistui-items-table-container">
                                        <table className="ozlistui-items-table">
                                            <thead>
                                                <tr>
                                                    <th>{translations.serialNo || '#'}</th>
                                                    <th>{translations.productName || 'Product'}</th>
                                                    <th>{translations.itemBatch || 'Batch'}</th>
                                                    <th>{translations.itemUnit || 'Unit'}</th>
                                                            <th>{translations.warranty || 'Warranty'}</th>
                                                            <th>{translations.issuanceDate || 'Issuance Date'}</th>
                                                    <th className="ozlistui-text-right">{translations.price || 'Price'}</th>
                                                    <th className="ozlistui-text-right">{translations.quantity || 'Qty'}</th>
                                                    <th className="ozlistui-text-right">{translations.total || 'Total'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedSale.items.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>{idx + 1}</td>
                                                        <td>{item.product_name}</td>
                                                        <td>{item.batch_no || '-'}</td>
                                                        <td>{item.unit || '-'}</td>
                                                        <td style={{ color: hasIssuedWarranty(item) ? '#15803d' : '#9ca3af', fontWeight: hasIssuedWarranty(item) ? '600' : '400' }}>{hasIssuedWarranty(item) ? `✓ ${item.warranty}` : 'Not issued'}</td>
                                                        <td>{hasIssuedWarranty(item) ? (item.warranty_issuance_date || '-') : '-'}</td>
                                                        <td className="ozlistui-text-right">{formatCurrency(item.price)}</td>
                                                        <td className="ozlistui-text-right">{Math.round(parseFloat(item.quantity) || 0)}</td>
                                                        <td className="ozlistui-text-right ozlistui-total-cell">{formatCurrency(item.total)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="ozlistui-modal-footer">
                            <button className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowViewModal(false)}>{translations.close || 'Close'}</button>
                            <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => { setShowViewModal(false); handlePrint(selectedSale); }}>
                                <FontAwesomeIcon icon={faPrint} />
                                {translations.print || 'Print'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedSale && (
                <div className={`ozlistui-modal-overlay ${darkMode ? 'ozlistui-dark' : ''}`} onClick={(e) => e.stopPropagation()}>
                    <div
                        ref={modalRef}
                        className="ozlistui-modal-content ozlistui-edit-modal-content"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'fixed',
                            left: modalPosition.x || '50%',
                            top: modalPosition.y || '50%',
                            transform: modalPosition.x === 0 && modalPosition.y === 0 ? 'translate(-50%, -50%)' : 'none',
                            width: modalSize.width,
                            minWidth: '350px',
                            maxWidth: '95vw',
                            maxHeight: '85vh'
                        }}
                    >
                        <div className="ozlistui-resize-handle-top" onMouseDown={(e) => startResize(e, 'n')}></div>
                        <div className="ozlistui-resize-handle-bottom" onMouseDown={(e) => startResize(e, 's')}></div>
                        <div className="ozlistui-resize-handle-left" onMouseDown={(e) => startResize(e, 'w')}></div>
                        <div className="ozlistui-resize-handle-right" onMouseDown={(e) => startResize(e, 'e')}></div>
                        <div className="ozlistui-resize-handle-nw" onMouseDown={(e) => startResize(e, 'nw')}></div>
                        <div className="ozlistui-resize-handle-ne" onMouseDown={(e) => startResize(e, 'ne')}></div>
                        <div className="ozlistui-resize-handle-sw" onMouseDown={(e) => startResize(e, 'sw')}></div>
                        <div className="ozlistui-resize-handle-se" onMouseDown={(e) => startResize(e, 'se')}></div>

                        <div className="ozlistui-modal-header" onMouseDown={startDrag}>
                            <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faEdit} /></div>
                            <div className="ozlistui-modal-title">
                                <h3>{translations.editSale || 'Edit Sale'}</h3>
                                <span className="ozlistui-sale-number-badge">#{selectedSale.bill_number}</span>
                            </div>
                            <div className="ozlistui-width-control-group">
                                {Object.entries(widthModes).map(([key, mode]) => (
                                    <button key={key} className={`ozlistui-width-btn ${currentWidthMode === key ? 'ozlistui-active' : ''}`} onClick={() => setWidthMode(key)} title={mode.label}>
                                        <FontAwesomeIcon icon={mode.icon} />
                                    </button>
                                ))}
                            </div>
                            <button className="ozlistui-close-btn" onClick={() => setShowEditModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <form onSubmit={handleUpdateSale}>
                            <div className="ozlistui-modal-body">
                                <div className="ozlistui-form-grid">
                                    <div className={`ozlistui-form-group ${editFormErrors.date ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="date"
                                                id="edit_date"
                                                name="date"
                                                value={editFormData.date}
                                                onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                                                max={getTodayDate()}
                                                placeholder=" "
                                                className={editFormErrors.date ? 'ozlistui-error' : ''}
                                            />
                                            <label htmlFor="edit_date">
                                                <FontAwesomeIcon icon={faCalendar} />
                                                {translations.date || 'Date'} <span className="ozlistui-required">*</span>
                                            </label>
                                        </div>
                                        {editFormErrors.date && (
                                            <div className="ozlistui-error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.date}
                                            </div>
                                        )}
                                    </div>

                                    <div className={`ozlistui-form-group ${editFormErrors.bill_number ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="text"
                                                id="edit_bill_number"
                                                name="bill_number"
                                                value={editFormData.bill_number}
                                                onChange={(e) => setEditFormData({ ...editFormData, bill_number: e.target.value })}
                                                placeholder=" "
                                                disabled={editLoading}
                                            />
                                            <label htmlFor="edit_bill_number">
                                                <FontAwesomeIcon icon={faFileInvoice} />
                                                {translations.billNumber || 'Bill Number'} <span className="ozlistui-required">*</span>
                                            </label>
                                        </div>
                                        {editFormErrors.bill_number && (
                                            <div className="ozlistui-error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.bill_number}
                                            </div>
                                        )}
                                    </div>

                                    <div className={`ozlistui-form-group ${editFormErrors.bn ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="text"
                                                id="edit_bn"
                                                name="bn"
                                                value={editFormData.bn}
                                                onChange={(e) => setEditFormData({ ...editFormData, bn: e.target.value })}
                                                placeholder=" "
                                                maxLength={100}
                                                pattern="[A-Za-z0-9]*"
                                                title="Letters and numbers only"
                                                className={editFormErrors.bn ? 'ozlistui-error' : ''}
                                            />
                                            <label htmlFor="edit_bn">
                                                <FontAwesomeIcon icon={faFileInvoice} />
                                                {translations.bn || 'BN'}
                                            </label>
                                        </div>
                                        {editFormErrors.bn && (
                                            <div className="ozlistui-error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.bn}
                                            </div>
                                        )}
                                    </div>

                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="text"
                                                id="edit_customer_name"
                                                name="customer_name"
                                                value={editFormData.customer_name}
                                                onChange={(e) => setEditFormData({ ...editFormData, customer_name: e.target.value })}
                                                placeholder=" "
                                            />
                                            <label htmlFor="edit_customer_name">
                                                <FontAwesomeIcon icon={faUser} />
                                                {translations.customerName || 'Customer Name'}
                                                <span> ({translations.optional || 'Optional'})</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="tel"
                                                id="edit_customer_phone"
                                                name="customer_phone"
                                                value={editFormData.customer_phone}
                                                onChange={(e) => setEditFormData({ ...editFormData, customer_phone: e.target.value })}
                                                placeholder=" "
                                                dir="ltr"
                                            />
                                            <label htmlFor="edit_customer_phone">
                                                <FontAwesomeIcon icon={faPhone} />
                                                {translations.phone || 'Phone Number'}
                                                <span> ({translations.optional || 'Optional'})</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-form-group ozlistui-full-width">
                                        <div className="ozlistui-input-wrapper">
                                            <textarea
                                                id="edit_customer_address"
                                                name="customer_address"
                                                value={editFormData.customer_address}
                                                onChange={(e) => setEditFormData({ ...editFormData, customer_address: e.target.value })}
                                                rows="2"
                                                placeholder=" "
                                            ></textarea>
                                            <label htmlFor="edit_customer_address">
                                                <FontAwesomeIcon icon={faMapMarkerAlt} />
                                                {translations.customerAddress || 'Address'}
                                                <span> ({translations.optional || 'Optional'})</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <select
                                                id="edit_payment_method"
                                                name="payment_method"
                                                value={editFormData.payment_method}
                                                onChange={(e) => setEditFormData({ ...editFormData, payment_method: e.target.value })}
                                                disabled
                                            >
                                                <option value="cash">{translations.cash || 'Cash'}</option>
                                                <option value="card">{translations.card || 'Card'}</option>
                                                <option value="upi">{translations.upi || 'UPI'}</option>
                                                <option value="bank_transfer">{translations.bankTransfer || 'Bank Transfer'}</option>
                                            </select>
                                            <label htmlFor="edit_payment_method">
                                                <FontAwesomeIcon icon={faCreditCard} />
                                                {translations.paymentMethod || 'Payment Method'}
                                            </label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-form-group ozlistui-full-width">
                                        <div className="ozlistui-edit-sale-items" role="region" aria-label="Editable sale items" tabIndex="0">
                                            <SaleItemsTable items={editFormData.items || []} onItemChange={updateEditSaleItem}
                                                onSelectProductForRow={selectEditSaleProduct} onRemove={removeEditSaleItem}
                                                onAdd={addEditSaleItem} errors={editFormErrors} disabled={editLoading}
                                                translations={translations} isRTL={isRTL} searchProductsAPI={searchEditSaleProducts}
                                                showToast={showToast} currentShop={currentShop} />
                                        </div>
                                        {editFormErrors.items && <div className="ozlistui-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {editFormErrors.items}</div>}
                                        <div className="ozlistui-scanner-note"><FontAwesomeIcon icon={faBarcode} /> {translations.scannerReady || 'Scanner ready while this modal is open'}</div>
                                    </div>

                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <input type="number" id="edit_tax" name="tax" value={editFormData.tax} onChange={(e) => setEditFormData(prev => ({ ...prev, tax: e.target.value, ...recalculateEditSale(prev.items, e.target.value, prev.discount, prev.paid_amount) }))} step="0.01" min="0" disabled={editLoading} placeholder="0" />
                                            <label htmlFor="edit_tax"><FontAwesomeIcon icon={faCalculator} />{translations.tax || 'Tax'}</label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <input type="text" id="edit_courier_name" name="courier_name" value={editFormData.courier_name} onChange={(e) => setEditFormData({ ...editFormData, courier_name: e.target.value })} disabled={editLoading} placeholder=" " />
                                            <label htmlFor="edit_courier_name">{translations.courier || 'Courier'}</label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <input type="text" id="edit_tracking_number" name="tracking_number" value={editFormData.tracking_number} onChange={(e) => setEditFormData({ ...editFormData, tracking_number: e.target.value })} disabled={editLoading} placeholder=" " />
                                            <label htmlFor="edit_tracking_number">{translations.trackingNumber || 'Tracking Number'}</label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <select id="edit_packing_status" name="packing_status" value={editFormData.packing_status} onChange={(e) => setEditFormData({ ...editFormData, packing_status: e.target.value })} disabled={editLoading}>
                                                <option value="unpacked">{translations.unpacked || 'Unpacked'}</option>
                                                <option value="partially_packed">{translations.partiallyPacked || 'Partially Packed'}</option>
                                                <option value="packed">{translations.packed || 'Packed'}</option>
                                            </select>
                                            <label htmlFor="edit_packing_status">{translations.packingStatus || 'Packing Status'}</label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <input type="number" id="edit_discount" name="discount" value={editFormData.discount} onChange={(e) => setEditFormData(prev => ({ ...prev, discount: e.target.value, ...recalculateEditSale(prev.items, prev.tax, e.target.value, prev.paid_amount) }))} step="0.01" min="0" disabled={editLoading} placeholder="0" />
                                            <label htmlFor="edit_discount"><FontAwesomeIcon icon={faTag} />{translations.discount || 'Discount'}</label>
                                        </div>
                                    </div>

                                    <div className={`ozlistui-form-group ${editFormErrors.grand_total ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="number"
                                                id="edit_grand_total"
                                                name="grand_total"
                                                value={editFormData.grand_total}
                                                readOnly
                                                step="1"
                                                min="0"
                                                placeholder="0"
                                            />
                                            <label htmlFor="edit_grand_total">
                                                <FontAwesomeIcon icon={faDollarSign} />
                                                {translations.grandTotal || 'Grand Total'} <span className="ozlistui-required">*</span>
                                            </label>
                                        </div>
                                        {editFormErrors.grand_total && (
                                            <div className="ozlistui-error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.grand_total}
                                            </div>
                                        )}
                                    </div>

                                    <div className={`ozlistui-form-group ${editFormErrors.paid_amount ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="number"
                                                id="edit_paid_amount"
                                                name="paid_amount"
                                                value={editFormData.paid_amount}
                                                onChange={(e) => {
                                                    const paid = e.target.value;
                                                    setEditFormData(prev => {
                                                        return { ...prev, paid_amount: paid, ...recalculateEditSale(prev.items, prev.tax, prev.discount, paid) };
                                                    });
                                                }}
                                                step="1"
                                                min="0"
                                                placeholder="0"
                                                disabled={Boolean(editFormData.payment_locked)}
                                                title={editFormData.payment_locked ? (translations.paymentLockedHint || 'Paid amount is preserved because this sale used customer credit or a previous-balance allocation') : ''}
                                            />
                                            <label htmlFor="edit_paid_amount">
                                                <FontAwesomeIcon icon={faMoneyBill} />
                                                {translations.paidAmount || 'Paid Amount'} <span className="ozlistui-required">*</span>
                                            </label>
                                        </div>
                                        {editFormErrors.paid_amount && (
                                            <div className="ozlistui-error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.paid_amount}
                                            </div>
                                        )}
                                        {editFormData.payment_locked && (
                                            <div className="ozlistui-scanner-note">
                                                {translations.paymentLockedNote || 'Paid amount is preserved because this sale affected customer credit or a previous balance.'}
                                            </div>
                                        )}
                                    </div>

                                    <div className="ozlistui-form-group ozlistui-balance-display">
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="text"
                                                id="edit_balance"
                                                value={formatCurrency(editFormData.balance)}
                                                readOnly
                                                className="ozlistui-readonly-input"
                                                style={{ color: parseFloat(editFormData.balance) > 0 ? '#d97706' : '#059669', fontWeight: 'bold' }}
                                            />
                                            <label htmlFor="edit_balance">
                                                <FontAwesomeIcon icon={faBalanceScale} />
                                                {translations.balance || 'Balance'}
                                            </label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-form-group ozlistui-full-width">
                                        <div className="ozlistui-input-wrapper">
                                            <textarea
                                                id="edit_notes"
                                                name="notes"
                                                value={editFormData.notes}
                                                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                                                rows="2"
                                                placeholder=" "
                                            ></textarea>
                                            <label htmlFor="edit_notes">
                                                <FontAwesomeIcon icon={faReceipt} />
                                                {translations.notes || 'Notes'}
                                                <span> ({translations.optional || 'Optional'})</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-edit-info-note">
                                        <p><FontAwesomeIcon icon={faInfoCircle} /> {translations.editableNotice || 'Edit bill details and line items. Totals update automatically from prices, quantities, tax, and discount.'}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="ozlistui-modal-footer">
                                <button type="button" className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowEditModal(false)}>
                                    <FontAwesomeIcon icon={faTimes} />
                                    {translations.cancel || 'Cancel'}
                                </button>
                                <button type="submit" className="ozlistui-btn ozlistui-btn-primary" disabled={editLoading}>
                                    {editLoading ? (
                                        <>
                                            <div className="ozlistui-btn-spinner"></div>
                                            <span>{translations.updating || 'Updating...'}</span>
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSave} />
                                            <span>{translations.saveChanges || 'Save Changes'}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className={`ozlistui-modal-overlay ${darkMode ? 'ozlistui-dark' : ''}`} onClick={(e) => e.stopPropagation()}>
                    <div className="ozlistui-modal-content ozlistui-delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="ozlistui-modal-header ozlistui-delete-header">
                            <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faExclamationTriangle} /></div>
                            <div className="ozlistui-modal-title"><h3>{translations.confirmDelete || 'Confirm Delete'}</h3></div>
                            <button className="ozlistui-close-btn" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
                        </div>
                        <div className="ozlistui-modal-body">
                            <div className="ozlistui-delete-message">
                                <p>{translations.deleteMessage || 'Are you sure you want to delete this sale?'}</p>
                                <p><FontAwesomeIcon icon={faExclamationTriangle} /> {translations.deleteWarning || 'This action cannot be undone.'}</p>
                            </div>
                        </div>
                        <div className="ozlistui-modal-footer">
                            <button type="button" className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} />{translations.noCancel || 'No, Cancel'}</button>
                            <button type="button" className="ozlistui-btn ozlistui-btn-delete" onClick={handleDelete}><FontAwesomeIcon icon={faTrash} />{translations.yesDelete || 'Yes, Delete'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SolarSpecialSalesList;
